/**
 * Inbound receiving workspace, touch-first dock kiosk.
 *
 * Horizontal line tiles (like transfer pack), labeled containers panel, and numpad.
 * Count onto a line, close container and print a 4x2 inventory label per bag or box.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Camera,
  ChevronDown,
  Eye,
  Loader2,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react";

import { ApiError } from "@/lib/api";
import { quantityUnitSuffix } from "@/lib/mrpSkuUnitDisplay";
import { HUB_CACHE_CHANNEL } from "@/lib/hubCacheChannels";
import { useHubCacheStream } from "@/lib/useHubCacheStream";
import { resolveGs1ManufacturerPartNumber } from "@integrator-src/lib/gs1-element-string";
import {
  appendAltNumpadDigit,
  characterForCompletedAltNumpad,
} from "@integrator-src/lib/wedge-alt-numpad";
import { applyReceiveQuantityModeKey } from "./inboundReceiveQuantityScan";
import { isLaborOwnerDeparting } from "@/lib/labor/laborOwnerDeparture";
import { LaborStationOperatorPresence } from "@/components/labor/LaborStationOperatorPresence";
import { HubBreadcrumbTitleRow } from "@/components/HubPageTitle";
import { InboundZplPrinterHeaderButton } from "./InboundZplPrinterHeaderButton";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { TapDisabledReason } from "@/components/ui/TapDisabledReason";
import { useHubToast } from "@/components/ui/HubToast";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { CameraCaptureModal } from "@/components/ui/CameraCaptureModal";
import { VoiceToTextMicButton } from "@/components/ui/VoiceToTextMicButton";
import { usePageBreadcrumb, useNavigationTrail } from "@/lib/navigationTrailContext";
import {
  INBOUND_RECEIVE_ARRIVAL_UNIT_KIND_SEARCH_KEY,
  INBOUND_RECEIVE_RECEIPT_ID_SEARCH_KEY,
  buildInboundReceiveNavigateTarget,
  mergeInboundReceiveArrivalPackageSearch,
  parseInboundReceiveArrivalPackageSearch,
} from "@/pages/shipping/inboundReceiveNavigation";
import { playPairFailureSound, playPairSuccessSound, playLaborIdleWarningSound } from "@/lib/floorStationPairAudio";
import {
  parseReceiveQuantityScan,
  primeReceiveFloorSpeechSynthesis,
  speakReceiveQuantity,
  speakReceiveSku,
} from "@/lib/receiveFloorSpeech";
import {
  FLOOR_PAGE_SCAN_TARGET_PROPS,
  useFloorPageScanCapture,
} from "@/lib/floorPageScanCapture";
import { cn } from "@/lib/utils";
import { openDocumentUrl } from "@/lib/openDocumentUrl";
import {
  PREPACK_TRANSFER_KIOSK_HEIGHT_CLASS,
  PREPACK_TRANSFER_NUMPAD_SIDE_STORAGE_KEY,
  readStoredTransferPackNumpadSide,
  TransferPackNumpad,
  TransferPackNumpadSideToggle,
  kioskMainPaneOrderClass,
  kioskNumpadPaneOrderClass,
  kioskNumpadSplitGridClass,
  type TransferPackInputMode,
  type TransferPackNumpadSide,
} from "@/pages/prepack/prepackTransferTouchUi";

import {
  closeInboundSubcontainer,
  deleteAllInboundPackingSlips,
  deleteInboundPackingSlip,
  fetchInboundDetail,
  fetchPackingSlipUrl,
  lookupReceiveArrivalScan,
  patchInboundReceiptOpenSubcontainerUnitKind,
  patchInboundSubcontainerUnitKind,
  unsealInboundSubcontainer,
  scanArrivalPackageOntoReceipt,
  inboundReceiptPackingSlips,
  formatNumber,
  printInboundInventoryLabelBatch,
  ensureInboundDraftReceipt,
  createInboundAdHocReceiptLine,
  patchInboundAdHocReceiptLineQuantity,
  splitInboundDamagedOrderLine,
  saveInboundReceipt,
  finishInboundReceiptArrivalContainer,
  uploadInboundPackingSlip,
  verifyInboundLineMpn,
  lookupInboundAmlByMpn,
  type InboundDetailResponse,
  type InboundLineRow,
  type InboundReceipt,
  type InboundSourceType,
} from "./inboundApi";
import {
  formatInboundArrivalUnitHeading,
  formatInboundSubcontainerHeading,
  inboundReceiveFocusedTitle,
  inboundReceiveOrderLabel,
  parseInboundArrivalUnitKind,
  parseInboundSubcontainerUnitKind,
  resolveOpenSubcontainerUnitKindFromReceipt,
  type InboundArrivalUnitKind,
} from "./inboundArrivalUnitKind";
import { inboundEachQuantityError } from "./inboundEach";
import { InboundContainerLaborProvider } from "./inboundContainerLaborContext";

import {
  InboundReceiveDamagedModal,
  InboundReceiveAmlLookupModal,
  InboundReceiveMpnMismatchModal,
  InboundReceiveNonSkuModal,
  InboundReceiveUnlistedSkuModal,
} from "./InboundReceiveExceptionModals";
import { InboundReceiveDockActionsMenu } from "./InboundReceiveDockActionsMenu";
import { inboundReceiveLaborPageKey } from "./inboundLaborPage";
import {
  closedQuantityForLine,
  closedQuantityForLineSku,
  openSubcontainerLines,
  RECEIVE_OPEN_CONTAINER_BLOCK_MESSAGE,
  receiveArrivalScanShouldOpenOtherOrder,
  receiveOpenContainerSelection,
  receivedQuantityAfterContainerEntry,
} from "./inboundReceiveSubcontainer";
import { resolveInboundReceiptForArrivalPackage } from "./inboundReceiveReceipt";
import { InboundReceiveMpnScan } from "./InboundReceiveMpnScan";
import {
  countingUnknownMpnPath,
  emptyInboundLineMpnDraftFields,
  INBOUND_RECEIVE_LINE_SCAN_PROMPT,
  INBOUND_UNKNOWN_PART_LABEL_SKU,
  lineRequiresMpnEntry,
  mpnEntryAllowsCounting,
  resolveInboundMpnEntryState,
} from "./inboundReceiveMpn";
import { InboundReceiveMpnEntryModal } from "./InboundReceiveMpnEntryModal";
import {
  inboundSupplierLabelMpnWasExtracted,
  parseInboundSupplierLabelQuantity,
  pickInboundOrderLinesForLabelScan,
  resolveInboundSupplierLabelMpn,
} from "./inboundReceiveLabelScan";
import { InboundReceiveSubcontainersPanel } from "./InboundReceiveSubcontainersPanel";
import {
  InboundReceiveLineTile,
  canonicalInboundLineForSku,
  collapseInboundLinesBySku,
  inboundLineIdsForSku,
  inboundLinesShareSku,
  resolveInboundReceiveLineId,
  sortInboundLinesStable,
} from "./inboundReceiveTouchUi";
import { InboundPackingSlipConfirmModal } from "./InboundPackingSlipConfirmModal";
import {
  InboundReceiveArrivalOrderPickModal,
  type InboundArrivalOrderPickSource,
} from "./InboundReceiveArrivalOrderPickModal";
import { useInboundZplPrinter } from "./inboundZplPrinter";
import { inboundReceiveFinishArrivalBlockReason } from "./inboundReceiveFinishArrival";
import {
  inboundReceiveClearLineTapReason,
  inboundReceiveDamagedTapReason,
  inboundReceiveLineTileTapReason,
  inboundReceiveMoreActionsTapReason,
  inboundReceiveNumpadTapReason,
} from "./inboundReceiveDisabledTap";
import {
  inboundReceiveSealBlockReason,
  inboundReceiveSealReady,
  type InboundReceiveDraftSaveSync,
} from "./inboundReceiveSealReady";
import {
  EMPTY_LINE_DRAFT,
  RECEIVE_PAGE_HOVER_HINT,
  buildInitialDrafts,
  buildReceiptSaveInput,
  inboundOpenSubcontainerInputLines,
  mergeSavedInboundReceiptIntoDetail,
  reconcileInboundDraftsAfterSave,
  consolidateInboundDuplicateSkuDrafts,
  countedQuantity,
  formatQuantityDraftDisplay,
  isInboundSourceType,
  isOrderLine,
  looksLikeArrivalPackageScan,
  resolveInboundOrderLineMpnDraft,
  unknownCountedQuantity,
  type LineDraft,
} from "./inboundReceivePageModel";

/**
 * Receiving-owned quantity readout with a CONSTANT height: the SKU line and
 * mode hint keep reserved slots whether or not a line is selected, so the
 * numpad below never shifts between states (the shared TransferPackQuantityReadout
 * grows and shrinks, which reads as sloppy here).
 */
function InboundQuantityReadout({
  selectedLine,
  currentCount,
  quantityDraft,
  replacing,
}: {
  selectedLine: InboundLineRow | null;
  currentCount: number;
  quantityDraft: string;
  replacing: boolean;
}): JSX.Element {
  const hint =
    selectedLine == null
      ? "Tap a line to count it"
      : replacing
        ? "Retype - replaces the line total on this receipt"
        : currentCount > 0
          ? `Enter adds to the open container (${formatNumber(currentCount)} ${quantityUnitSuffix(selectedLine.unit_of_measure)} so far)`
          : "Key the quantity, then Enter";
  return (
    <div className="shrink-0 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Selected SKU</p>
          <p className="truncate font-mono text-sm font-semibold text-slate-900">{selectedLine?.sku ?? "-"}</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Qty</p>
          <p className="flex items-baseline justify-end gap-1">
            <span className="min-w-[3.5rem] text-3xl font-bold tabular-nums leading-none text-slate-900 lg:text-4xl">
              {selectedLine == null ? "0" : formatQuantityDraftDisplay(quantityDraft)}
            </span>
            {selectedLine != null ? (
              <span className="text-sm font-semibold text-slate-600">
                {quantityUnitSuffix(selectedLine.unit_of_measure)}
              </span>
            ) : null}
          </p>
        </div>
      </div>
      <div className="mt-1 flex h-5 items-center">
        <p className={cn("min-w-0 truncate text-[11px]", replacing ? "font-medium text-brand-800" : "text-slate-500")}>
          {hint}
        </p>
      </div>
    </div>
  );
}

export function InboundReceivePage(): JSX.Element {
  const params = useParams<{ source: string; id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { trail } = useNavigationTrail();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const {
    station,
    setStation,
    selectedPrinter: selectedZplPrinter,
    unavailableMessage, documents,
    printersQuery,
  } = useInboundZplPrinter();
  const scanInputRef = useRef<HTMLInputElement>(null);
  const altNumpadRef = useRef("");
  const quantityScanBufferRef = useRef("");
  const quantityAltNumpadRef = useRef("");

  const sourceType = isInboundSourceType(params.source) ? params.source : null;
  const sourceId = params.id ?? "";
  const receiveLaborPage = inboundReceiveLaborPageKey(sourceType, sourceId);
  const receiveLocationState =
    (location.state as {
      arrivalScan?: string;
      arrivalPackageNo?: number;
      arrivalPackageId?: string;
      arrivalUnitKind?: InboundArrivalUnitKind;
    } | null) ?? null;
  const arrivalFromSearch = parseInboundReceiveArrivalPackageSearch(searchParams);
  const preferredReceiptId = searchParams.get(INBOUND_RECEIVE_RECEIPT_ID_SEARCH_KEY)?.trim() || null;
  const arrivalScanFromNav = receiveLocationState?.arrivalScan?.trim() ?? null;
  const arrivalPackageNoFromNav =
    arrivalFromSearch?.arrivalPackageNo ?? receiveLocationState?.arrivalPackageNo ?? null;
  const arrivalPackageIdFromNav =
    arrivalFromSearch?.arrivalPackageId ?? receiveLocationState?.arrivalPackageId?.trim() ?? null;
  const arrivalUnitKindFromNav =
    receiveLocationState?.arrivalUnitKind ?? arrivalFromSearch?.arrivalUnitKind;

  const [focusedArrivalPackage, setFocusedArrivalPackage] = useState<{
    packageId: string;
    packageNo: number;
    unitKind: InboundArrivalUnitKind;
  } | null>(() => {
    const packageId = arrivalPackageIdFromNav;
    const packageNo = arrivalPackageNoFromNav;
    if (
      packageId != null
      && packageId !== ""
      && packageNo != null
      && Number.isFinite(packageNo)
      && packageNo > 0
    ) {
      return {
        packageId,
        packageNo,
        unitKind: parseInboundArrivalUnitKind(arrivalUnitKindFromNav),
      };
    }
    return null;
  });

  const [drafts, setDrafts] = useState<Record<string, LineDraft>>({});
  const [notes, setNotes] = useState("");
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [editingReplace, setEditingReplace] = useState(false);
  const [quantityDraft, setQuantityDraft] = useState("");
  const [inputMode, setInputMode] = useState<TransferPackInputMode>("scan");
  const [numpadSide, setNumpadSide] = useState<TransferPackNumpadSide>(readStoredTransferPackNumpadSide);
  const [mpnVerifying, setMpnVerifying] = useState(false);
  const [manufacturerSeed, setManufacturerSeed] = useState<string | null>(null);
  const pendingSupplierLabelQuantityRef = useRef<number | null>(null);
  const [lineQuery, setLineQuery] = useState("");
  const [scanFeedback, setScanFeedback] = useState<string | null>(null);
  const [packingSlipMenuOpen, setPackingSlipMenuOpen] = useState(false);
  const [packingSlipCameraOpen, setPackingSlipCameraOpen] = useState(false);
  const [pendingPackingSlipFile, setPendingPackingSlipFile] = useState<File | null>(null);
  const [pendingPackingSlipPageLabel, setPendingPackingSlipPageLabel] = useState<string | null>(null);
  const [packingSlipConfirmOpen, setPackingSlipConfirmOpen] = useState(false);
  const pendingPackingSlipQueueRef = useRef<Array<{ file: File; pageLabel: string }>>([]);
  const packingSlipAddAnotherAfterSaveRef = useRef(false);
  const [printingSubcontainerNo, setPrintingSubcontainerNo] = useState<number | null>(null);
  const [pendingOpenSubcontainerUnitKind, setPendingOpenSubcontainerUnitKind] =
    useState<InboundArrivalUnitKind | null>(null);
  const [sealedUnitKindPendingId, setSealedUnitKindPendingId] = useState<string | null>(null);
  const [unsealPendingId, setUnsealPendingId] = useState<string | null>(null);
  const [damagedModalOpen, setDamagedModalOpen] = useState(false);
  const [unlistedModalOpen, setUnlistedModalOpen] = useState(false);
  const [nonSkuModalOpen, setNonSkuModalOpen] = useState(false);
  const [amlLookupModalOpen, setAmlLookupModalOpen] = useState(false);
  const [arrivalOrderPick, setArrivalOrderPick] = useState<{
    scan: string;
    packageId: string;
    packageNo: number;
    vendorName: string | null;
    unitKind: string;
    sources: InboundArrivalOrderPickSource[];
  } | null>(null);
  const [unlistedSkuPrefill, setUnlistedSkuPrefill] = useState("");
  const [exceptionPending, setExceptionPending] = useState(false);
  const [draftSaveSync, setDraftSaveSync] = useState<InboundReceiveDraftSaveSync>("synced");
  const [draftSaveError, setDraftSaveError] = useState<string | null>(null);
  const draftSaveSyncRef = useRef(draftSaveSync);
  draftSaveSyncRef.current = draftSaveSync;
  const activeReceiptIdRef = useRef<string | null>(null);
  const containerLaborWriteRef = useRef<(() => void) | null>(null);
  const pendingArrivalScanRef = useRef<string | null>(
    arrivalPackageIdFromNav != null && arrivalPackageIdFromNav !== "" ? null : arrivalScanFromNav,
  );
  const packingSlipMenuRef = useRef<HTMLDivElement>(null);
  const packingSlipUploadInputRef = useRef<HTMLInputElement>(null);
  const activeArrivalPackageId = arrivalPackageIdFromNav ?? focusedArrivalPackage?.packageId ?? null;
  const detailQuery = useQuery({
    queryKey: ["shipping", "inbound", sourceType, sourceId, activeArrivalPackageId ?? ""],
    queryFn: () =>
      fetchInboundDetail(sourceType as InboundSourceType, sourceId, activeArrivalPackageId),
    enabled: sourceType !== null && sourceId !== "",
  });
  const detail = detailQuery.data;
  const lines = useMemo(() => detail?.lines ?? [], [detail]);
  const unitOfMeasureBySourceItemId = useMemo(() => {
    const units: Record<string, string | null> = {};
    for (const line of lines) {
      units[line.source_item_id] = line.unit_of_measure ?? null;
    }
    return units;
  }, [lines]);
  const filteredLines = useMemo(() => {
    const needle = lineQuery.trim().toLowerCase();
    const matched =
      needle === ""
        ? lines
        : lines.filter((line) => {
            const haystack = [
              line.sku,
              line.po_sku,
              line.description,
              line.line_number != null ? String(line.line_number) : "",
            ]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();
            return haystack.includes(needle);
          });
    return collapseInboundLinesBySku(sortInboundLinesStable(matched));
  }, [lineQuery, lines]);
  const receiptView = useMemo(
    () =>
      detail != null
        ? resolveInboundReceiptForArrivalPackage(
            detail.receipts,
            activeArrivalPackageId,
            preferredReceiptId,
          )
        : null,
    [activeArrivalPackageId, detail, preferredReceiptId],
  );
  const activeReceipt = receiptView?.receipt ?? null;
  activeReceiptIdRef.current = activeReceipt?.id ?? null;
  const receiptReadOnly = receiptView?.readOnly ?? false;
  const mpnEntryModalOpen = useMemo(() => {
    if (receiptReadOnly || selectedLineId == null) return false;
    const line = lines.find((row) => row.source_item_id === selectedLineId);
    if (line == null || !lineRequiresMpnEntry(line)) return false;
    const draft = drafts[selectedLineId] ?? EMPTY_LINE_DRAFT;
    return resolveInboundMpnEntryState({ line, draft }) === "pending";
  }, [drafts, lines, receiptReadOnly, selectedLineId]);
  const mpnMismatchModalOpen = useMemo(() => {
    if (receiptReadOnly || selectedLineId == null) return false;
    const line = lines.find((row) => row.source_item_id === selectedLineId);
    if (line == null) return false;
    const draft = drafts[selectedLineId] ?? EMPTY_LINE_DRAFT;
    return resolveInboundMpnEntryState({ line, draft }) === "blocked";
  }, [drafts, lines, receiptReadOnly, selectedLineId]);
  const receiveTypingModalOpen =
    mpnEntryModalOpen
    || mpnMismatchModalOpen
    || damagedModalOpen
    || unlistedModalOpen
    || nonSkuModalOpen
    || amlLookupModalOpen
    || packingSlipConfirmOpen
    || arrivalOrderPick != null;

  useEffect(() => {
    if (mpnEntryModalOpen || mpnMismatchModalOpen) {
      scanInputRef.current?.blur();
      return;
    }
    if (!receiveTypingModalOpen) {
      requestAnimationFrame(() => {
        scanInputRef.current?.focus({ preventScroll: true });
      });
    }
  }, [mpnEntryModalOpen, mpnMismatchModalOpen, receiveTypingModalOpen]);
  const receiptOpenSubcontainerUnitKind = useMemo(() => {
    return resolveOpenSubcontainerUnitKindFromReceipt(activeReceipt?.open_subcontainer_unit_kind).kind;
  }, [activeReceipt?.open_subcontainer_unit_kind]);
  const openSubcontainerUnitKind = pendingOpenSubcontainerUnitKind ?? receiptOpenSubcontainerUnitKind;
  const openSubcontainerUnitKindUserSet = openSubcontainerUnitKind != null;

  useEffect(() => {
    if (
      pendingOpenSubcontainerUnitKind != null
      && receiptOpenSubcontainerUnitKind === pendingOpenSubcontainerUnitKind
    ) {
      setPendingOpenSubcontainerUnitKind(null);
    }
  }, [pendingOpenSubcontainerUnitKind, receiptOpenSubcontainerUnitKind]);

  const subcontainers = useMemo(() => activeReceipt?.subcontainers ?? [], [activeReceipt]);
  const closedItems = useMemo(
    () =>
      subcontainers.flatMap((subcontainer) =>
        subcontainer.items.map((item) => ({
          source_item_id: item.source_item_id,
          sku: item.sku,
          quantity: Number.parseFloat(item.quantity) || 0,
        })),
      ),
    [subcontainers],
  );
  const closedQuantityForDraftLineSku = useCallback(
    (lineId: string, sealedSku: string): number => {
      const line = lines.find((row) => row.source_item_id === lineId);
      const lineIds =
        line != null && isOrderLine(line) ? inboundLineIdsForSku(line.sku, lines) : [lineId];
      return lineIds.reduce(
        (sum, id) => sum + closedQuantityForLineSku(id, sealedSku, closedItems),
        0,
      );
    },
    [closedItems, lines],
  );
  const nextReceivedAfterEntry = useCallback(
    (lineId: string, previous: number, entered: number, replaceOpenQuantity: boolean, sealedSku: string) =>
      receivedQuantityAfterContainerEntry({
        alreadySealedQuantity: closedQuantityForDraftLineSku(lineId, sealedSku),
        currentReceivedQuantity: previous,
        enteredQuantity: entered,
        replaceOpenQuantity,
      }),
    [closedQuantityForDraftLineSku],
  );
  const openContainerLines = useMemo(
    () =>
      openSubcontainerLines({
        lines: inboundOpenSubcontainerInputLines(lines, drafts, closedItems),
        closedItems,
      }),
    [closedItems, drafts, lines],
  );
  const openContainerCount = useMemo(
    () => openContainerLines.reduce((sum, line) => sum + line.quantity, 0),
    [openContainerLines],
  );
  const labelPrinterSelected = selectedZplPrinter.trim() !== "";
  const containerTypeSelected = openSubcontainerUnitKindUserSet && openSubcontainerUnitKind != null;
  const sealReady = useMemo(
    () =>
      inboundReceiveSealReady({
        openContainerCount,
        labelPrinterSelected,
        containerTypeSelected,
        draftSaveSync,
      }),
    [containerTypeSelected, draftSaveSync, labelPrinterSelected, openContainerCount],
  );
  const sealBlockReason = useMemo(
    () =>
      inboundReceiveSealBlockReason({
        openContainerCount,
        labelPrinterSelected,
        containerTypeSelected,
        draftSaveSync,
        draftSaveError,
      }),
    [containerTypeSelected, draftSaveError, draftSaveSync, labelPrinterSelected, openContainerCount],
  );
  const scannedArrivalPackages = activeReceipt?.arrival_packages ?? [];
  const hasScannedArrivalPackage =
    scannedArrivalPackages.length > 0
    || (activeArrivalPackageId != null && activeArrivalPackageId.trim() !== "");
  const arrivalPackageUnitKindFromReceipt = useMemo(() => {
    const packageId = activeArrivalPackageId;
    if (packageId != null && packageId !== "") {
      const match = scannedArrivalPackages.find((row) => row.package_id === packageId);
      if (match != null) {
        return parseInboundArrivalUnitKind(match.unit_kind);
      }
    }
    if (scannedArrivalPackages.length === 1) {
      return parseInboundArrivalUnitKind(scannedArrivalPackages[0]!.unit_kind);
    }
    return null;
  }, [activeArrivalPackageId, scannedArrivalPackages]);
  const packingSlips = useMemo(() => inboundReceiptPackingSlips(activeReceipt), [activeReceipt]);
  const packingSlipCount = packingSlips.length;
  const hasPackingSlip = packingSlipCount > 0;

  const orderLabel = useMemo(
    () =>
      inboundReceiveOrderLabel({
        sourceNumber: detail?.source.source_number,
        sourceId,
      }),
    [detail?.source.source_number, sourceId],
  );
  const pageTitle = useMemo(
    () =>
      inboundReceiveFocusedTitle({
        sourceNumber: detail?.source.source_number,
        sourceId,
      }),
    [detail?.source.source_number, sourceId],
  );

  const activeArrivalContainer = useMemo(() => {
    if (focusedArrivalPackage != null) {
      return focusedArrivalPackage;
    }
    if (arrivalPackageNoFromNav != null && Number.isFinite(arrivalPackageNoFromNav) && arrivalPackageNoFromNav > 0) {
      return {
        packageNo: arrivalPackageNoFromNav,
        unitKind:
          arrivalPackageUnitKindFromReceipt ?? parseInboundArrivalUnitKind(arrivalUnitKindFromNav),
      };
    }
    if (scannedArrivalPackages.length === 1) {
      return {
        packageNo: scannedArrivalPackages[0]!.package_no,
        unitKind: parseInboundArrivalUnitKind(scannedArrivalPackages[0]!.unit_kind),
      };
    }
    return null;
  }, [
    arrivalPackageNoFromNav,
    arrivalPackageUnitKindFromReceipt,
    arrivalUnitKindFromNav,
    focusedArrivalPackage,
    scannedArrivalPackages,
  ]);

  const arrivalContainerHeading =
    activeArrivalContainer == null
      ? null
      : formatInboundArrivalUnitHeading(activeArrivalContainer.unitKind, activeArrivalContainer.packageNo);

  usePageBreadcrumb(orderLabel);

  useEffect(() => {
    const packageId = arrivalPackageIdFromNav;
    const packageNo = arrivalPackageNoFromNav;
    if (
      packageId != null
      && packageId !== ""
      && packageNo != null
      && Number.isFinite(packageNo)
      && packageNo > 0
    ) {
      setFocusedArrivalPackage((previous) => {
        const unitKind =
          arrivalUnitKindFromNav != null
            ? parseInboundArrivalUnitKind(arrivalUnitKindFromNav)
            : (previous?.packageId === packageId
              ? previous.unitKind
              : arrivalPackageUnitKindFromReceipt ?? parseInboundArrivalUnitKind(undefined));
        return { packageId, packageNo, unitKind };
      });
    }
  }, [
    arrivalPackageIdFromNav,
    arrivalPackageNoFromNav,
    arrivalPackageUnitKindFromReceipt,
    arrivalUnitKindFromNav,
  ]);

  useEffect(() => {
    if (focusedArrivalPackage == null) return;
    const currentId = searchParams.get("arrival_package_id")?.trim() ?? "";
    const currentNoRaw = searchParams.get("arrival_package_no");
    const currentNo = currentNoRaw != null ? Number.parseInt(currentNoRaw, 10) : Number.NaN;
    const currentUnitKind = searchParams.get(INBOUND_RECEIVE_ARRIVAL_UNIT_KIND_SEARCH_KEY)?.trim() ?? "";
    if (
      currentId === focusedArrivalPackage.packageId
      && Number.isFinite(currentNo)
      && currentNo === focusedArrivalPackage.packageNo
      && currentUnitKind === focusedArrivalPackage.unitKind
    ) {
      return;
    }
    setSearchParams(
      (previous) =>
        mergeInboundReceiveArrivalPackageSearch(previous, {
          arrivalPackageId: focusedArrivalPackage.packageId,
          arrivalPackageNo: focusedArrivalPackage.packageNo,
          arrivalUnitKind: focusedArrivalPackage.unitKind,
        }),
      { replace: true },
    );
  }, [focusedArrivalPackage, searchParams, setSearchParams]);

  /* Seed drafts once per source + dock crate so background refetches never clobber in-progress counting. */
  const seededSourceRef = useRef<string | null>(null);

  /* Reset counting state when the dock crate changes so crate 2 never shows crate 1 drafts. */
  useLayoutEffect(() => {
    seededSourceRef.current = null;
    setDrafts({});
    setNotes("");
    setSelectedLineId(null);
    setQuantityDraft("");
    setInputMode("scan");
    setScanFeedback(null);
  }, [activeArrivalPackageId]);

  useEffect(() => {
    if (!detail) return;
    const seedKey = `${detail.source.source_type}:${detail.source.source_id}:${activeArrivalPackageId ?? ""}`;
    if (seededSourceRef.current === seedKey) return;
    seededSourceRef.current = seedKey;
    setDrafts(consolidateInboundDuplicateSkuDrafts(detail.lines, buildInitialDrafts(detail.lines)));
    setNotes(activeReceipt?.notes ?? "");
    setDraftSaveSync("synced");
    setDraftSaveError(null);
  }, [activeArrivalPackageId, activeReceipt?.notes, detail]);

  useEffect(() => {
    if (selectedLineId == null) return;
    const line = lines.find((row) => row.source_item_id === selectedLineId);
    if (line == null) return;
    const canonicalId = resolveInboundReceiveLineId(line, lines);
    if (canonicalId !== selectedLineId) {
      setSelectedLineId(canonicalId);
    }
  }, [lines, selectedLineId]);

  /* ---- auto-save (debounced, serialized) -------------------------------------- */

  const stateRef = useRef({ drafts, notes, lines });
  stateRef.current = { drafts, notes, lines };
  const receiptLiveChannel = useMemo(
    () => HUB_CACHE_CHANNEL.shippingInboundReceipt(sourceType ?? "", sourceId, activeArrivalPackageId),
    [activeArrivalPackageId, sourceId, sourceType],
  );
  useHubCacheStream({
    channels: [receiptLiveChannel],
    enabled: sourceType !== null && sourceId !== "",
    onEvent: (event) => {
      if (event.phase !== "invalidated" || sourceType == null) return;
      const detailKey = ["shipping", "inbound", sourceType, sourceId, activeArrivalPackageId ?? ""] as const;
      void queryClient.fetchQuery({
        queryKey: detailKey,
        queryFn: () => fetchInboundDetail(sourceType, sourceId, activeArrivalPackageId),
      }).then((refreshed) => {
        const liveDrafts = reconcileInboundDraftsAfterSave(refreshed.lines, stateRef.current.drafts);
        setDrafts(liveDrafts);
        stateRef.current = { ...stateRef.current, drafts: liveDrafts, lines: refreshed.lines };
      });
    },
  });
  const saveTimerRef = useRef<number | null>(null);
  const saveQueueRef = useRef<Promise<InboundReceipt | null>>(Promise.resolve(null));

  const runPersistDraft = useCallback(async (onlySourceItemIds?: readonly string[]): Promise<InboundReceipt | null> => {
    if (receiptReadOnly) return null;
    if (sourceType == null || sourceId === "") return null;
    const current = stateRef.current;
    const seedKey = `${sourceType}:${sourceId}:${activeArrivalPackageId ?? ""}`;
    if (seededSourceRef.current !== seedKey) return null;

    setDraftSaveSync("saving");
    try {
      const saved = await saveInboundReceipt(
        buildReceiptSaveInput({
          sourceType,
          sourceId,
          arrivalPackageId: activeArrivalPackageId ?? null,
          notes: current.notes,
          lines: current.lines,
          drafts: current.drafts,
          closedItems,
          onlySourceItemIds,
        }),
      );
      if (saved != null) {
        containerLaborWriteRef.current?.();
        const adHocPatches = current.lines
          .filter((line) => line.line_kind === "unlisted_sku" || line.line_kind === "non_sku")
          .map((line) => {
            const receiptItemId = line.receipt_item_id;
            if (receiptItemId == null || receiptItemId.trim() === "") return null;
            return patchInboundAdHocReceiptLineQuantity(
              receiptItemId,
              countedQuantity(current.drafts[line.source_item_id]),
            );
          })
          .filter((promise): promise is Promise<void> => promise != null);
        if (adHocPatches.length > 0) {
          await Promise.all(adHocPatches);
        }
        if (sourceType != null) {
          const detailKey = ["shipping", "inbound", sourceType, sourceId, activeArrivalPackageId ?? ""] as const;
          queryClient.setQueryData<InboundDetailResponse>(detailKey, (previous) =>
            previous == null ? previous : mergeSavedInboundReceiptIntoDetail(previous, saved),
          );
        }
      }
      setDraftSaveSync("synced");
      setDraftSaveError(null);
      return saved;
    } catch (error) {
      const message = error instanceof ApiError ? error.message : String(error);
      setDraftSaveSync("error");
      setDraftSaveError(message);
      if (!isLaborOwnerDeparting()) {
        showToast({ message, tone: "error" });
      }
      return null;
    }
  }, [activeArrivalPackageId, closedItems, queryClient, receiptReadOnly, showToast, sourceId, sourceType]);

  const enqueuePersistDraft = useCallback((onlySourceItemIds?: readonly string[]): Promise<InboundReceipt | null> => {
    const next = saveQueueRef.current.then(() => runPersistDraft(onlySourceItemIds));
    saveQueueRef.current = next.catch(() => null);
    return next;
  }, [runPersistDraft]);

  const flushDraftSave = useCallback(async (onlySourceItemIds?: readonly string[]): Promise<InboundReceipt | null> => {
    if (saveTimerRef.current != null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    return enqueuePersistDraft(onlySourceItemIds);
  }, [enqueuePersistDraft]);

  /* ---- selection, scan, numpad ------------------------------------------------ */

  const returnToPickMode = useCallback(() => {
    setInputMode("scan");
    setEditingReplace(false);
  }, []);

  const selectLine = useCallback(
    (lineId: string) => {
      if (receiveOpenContainerSelection(openContainerLines.length) === "blocked") {
        playPairFailureSound();
        setScanFeedback(RECEIVE_OPEN_CONTAINER_BLOCK_MESSAGE);
        return;
      }
      setDrafts((current) => {
        const nextDraft = { ...(current[lineId] ?? EMPTY_LINE_DRAFT), ...emptyInboundLineMpnDraftFields() };
        const next = { ...current, [lineId]: nextDraft };
        stateRef.current = { ...stateRef.current, drafts: next };
        return next;
      });
      setSelectedLineId(lineId);
      setEditingReplace(false);
      setQuantityDraft("");
      setScanFeedback(null);
      setInputMode("quantity");
    },
    [openContainerLines],
  );

  useEffect(() => {
    if (openContainerLines.length === 0) return;
    const openLine = openContainerLines[0];
    if (openLine == null) return;
    const line = lines.find((row) => row.source_item_id === openLine.source_item_id);
    setSelectedLineId(line != null ? resolveInboundReceiveLineId(line, lines) : openLine.source_item_id);
    setEditingReplace(true);
    setInputMode("quantity");
  }, [lines, openContainerLines]);

  const focusOrderLineFromCpn = useCallback(
    (cpn: string) => {
      const trimmedCpn = cpn.trim();
      if (trimmedCpn === "") return;
      setLineQuery(trimmedCpn);
      const match =
        canonicalInboundLineForSku(trimmedCpn, lines)
        ?? lines.find((line) => line.sku.trim().toLowerCase() === trimmedCpn.toLowerCase());
      if (match == null) {
        playPairFailureSound();
        setUnlistedSkuPrefill(trimmedCpn);
        setScanFeedback(
          `CPN ${trimmedCpn} is not on this order. Add it as an unlisted SKU or pick a line below.`,
        );
        return;
      }
      playPairSuccessSound();
      speakReceiveSku(match.sku);
      selectLine(resolveInboundReceiveLineId(match, lines));
    },
    [lines, selectLine],
  );

  const processArrivalPackageScan = useCallback(
    async (scanned: string, options?: { silent?: boolean }): Promise<boolean> => {
      if (sourceType == null || sourceId === "") return false;
      const trimmed = scanned.trim();
      if (trimmed === "") return false;
      try {
        const lookup = await lookupReceiveArrivalScan(trimmed);
        if (lookup.kind !== "arrival_package") return false;
        const resolved = lookup.resolved_source;
        if (
          receiveArrivalScanShouldOpenOtherOrder({
            currentSourceType: sourceType,
            currentSourceId: sourceId,
            resolved,
            resolvedSources: lookup.resolved_sources,
          })
        ) {
          const pickSources =
            lookup.resolved_sources.length > 0
              ? lookup.resolved_sources
              : resolved != null
                ? [resolved]
                : [];
          if (pickSources.length === 0) {
            playPairFailureSound();
            setScanFeedback(
              `ARRIVED carton ${lookup.pkg.package_no} is not routed yet. Use Receive queue scan to pick PO, transfer, RMA, or non-PO.`,
            );
            return true;
          }
          const allPurchaseOrders =
            pickSources.length > 1
            && pickSources.every((source) => source.source_type === "purchase_order");
          if (pickSources.length > 1 && !allPurchaseOrders) {
            setArrivalOrderPick({
              scan: trimmed,
              packageId: lookup.pkg.package_id,
              packageNo: lookup.pkg.package_no,
              vendorName: lookup.pkg.vendor_name,
              unitKind: lookup.pkg.unit_kind,
              sources: pickSources,
            });
            return true;
          }
          const orderedSources = allPurchaseOrders
            ? [...pickSources].sort(
                (left, right) => Number(left.source_id) - Number(right.source_id),
              )
            : pickSources;
          const only = orderedSources[0]!;
          const target = buildInboundReceiveNavigateTarget(only.source_type, only.source_id, {
            arrivalPackageId: lookup.pkg.package_id,
            arrivalPackageNo: lookup.pkg.package_no,
            arrivalScan: trimmed,
            arrivalUnitKind: parseInboundArrivalUnitKind(lookup.pkg.unit_kind),
          });
          navigate({ pathname: target.pathname, search: target.search }, { state: target.state });
          return true;
        }
        const packageId = lookup.pkg.package_id;
        if (
          activeArrivalPackageId != null
          && activeArrivalPackageId !== ""
          && packageId !== activeArrivalPackageId
        ) {
          playPairFailureSound();
          setScanFeedback(
            "You are still receiving this box. Go back to the receive list and open the other box there.",
          );
          return true;
        }
        const alreadyAttached = scannedArrivalPackages.some((row) => row.package_id === packageId);
        if (alreadyAttached) {
          setFocusedArrivalPackage({
            packageId,
            packageNo: lookup.pkg.package_no,
            unitKind: parseInboundArrivalUnitKind(lookup.pkg.unit_kind),
          });
          if (!options?.silent) {
            setScanFeedback(null);
          }
          return true;
        }
        const current = stateRef.current;
        const saved =
          activeReceipt
          ?? await ensureInboundDraftReceipt(
            buildReceiptSaveInput({
              sourceType,
              sourceId,
              arrivalPackageId: activeArrivalPackageId ?? packageId,
              notes: current.notes,
              lines: current.lines,
              drafts: current.drafts,
              closedItems,
              retainEmptyDraft: true,
            }),
          );
        await scanArrivalPackageOntoReceipt(saved.id, trimmed);
        setFocusedArrivalPackage({
          packageId,
          packageNo: lookup.pkg.package_no,
          unitKind: parseInboundArrivalUnitKind(lookup.pkg.unit_kind),
        });
        if (!options?.silent) {
          playPairSuccessSound();
          setScanFeedback(`ARRIVED carton ${lookup.pkg.package_no} matched to this receipt.`);
        }
        setLineQuery("");
        void queryClient.invalidateQueries({
          queryKey: ["shipping", "inbound", sourceType, sourceId, activeArrivalPackageId ?? packageId],
        });
        return true;
      } catch (error) {
        playPairFailureSound();
        setScanFeedback(error instanceof ApiError ? error.message : String(error));
        return true;
      }
    },
    [activeArrivalPackageId, activeReceipt, navigate, queryClient, scannedArrivalPackages, sourceId, sourceType],
  );

  useEffect(() => {
    if (activeArrivalPackageId == null || activeArrivalPackageId === "" || detail == null) return;
    const alreadyLinked = (activeReceipt?.arrival_packages ?? []).some(
      (row) => row.package_id === activeArrivalPackageId,
    );
    if (alreadyLinked) return;
    const pending = pendingArrivalScanRef.current;
    if (pending == null || pending === "") return;
    pendingArrivalScanRef.current = null;
    void processArrivalPackageScan(pending, { silent: true });
  }, [activeArrivalPackageId, activeReceipt?.arrival_packages, detail, processArrivalPackageScan]);

  useEffect(() => {
    if (receiptReadOnly || sourceType == null || sourceId === "") return;
    if (activeArrivalPackageId == null || activeArrivalPackageId.trim() === "") return;
    if (activeReceipt != null) return;
    if (detail == null) return;
    const seedKey = `${sourceType}:${sourceId}:${activeArrivalPackageId}`;
    if (seededSourceRef.current !== seedKey) return;
    let cancelled = false;
    void (async () => {
      try {
        const current = stateRef.current;
        await ensureInboundDraftReceipt(
          buildReceiptSaveInput({
            sourceType,
            sourceId,
            arrivalPackageId: activeArrivalPackageId,
            notes: current.notes,
            lines: current.lines,
            drafts: current.drafts,
            closedItems,
            retainEmptyDraft: true,
          }),
        );
        if (!cancelled) {
          void queryClient.invalidateQueries({
            queryKey: ["shipping", "inbound", sourceType, sourceId, activeArrivalPackageId],
          });
        }
      } catch {
        // Counting will retry draft creation on first save.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activeArrivalPackageId,
    activeReceipt,
    detail,
    queryClient,
    receiptReadOnly,
    sourceId,
    sourceType,
  ]);

  const appendDigit = useCallback((digit: string) => {
    if (selectedLineId == null) return;
    setQuantityDraft((current) => {
      const next = current === "0" ? digit : `${current}${digit}`;
      return next.length > 6 ? current : next;
    });
  }, [selectedLineId]);

  const updateDraft = useCallback(
    (lineId: string, patch: Partial<LineDraft>, _autoSave = true) => {
      setDrafts((current) => {
        const nextDraft = { ...(current[lineId] ?? EMPTY_LINE_DRAFT), ...patch };
        const next = { ...current, [lineId]: nextDraft };
        // Keep stateRef in lockstep so an immediate flush (numpad Enter / seal)
        // never persists the pre-update count.
        stateRef.current = { ...stateRef.current, drafts: next };
        return next;
      });
    },
    [],
  );

  const applyLabelQuantityToLine = useCallback(
    (lineId: string, line: InboundLineRow, quantity: number): void => {
      const draft = stateRef.current.drafts[lineId] ?? EMPTY_LINE_DRAFT;
      const mpnState = lineRequiresMpnEntry(line)
        ? resolveInboundMpnEntryState({ line, draft })
        : "not_required";
      const unknownPath = countingUnknownMpnPath(mpnState);
      if (unknownPath) {
        const previous = unknownCountedQuantity(draft);
        const nextCount = nextReceivedAfterEntry(
          lineId,
          previous,
          quantity,
          false,
          INBOUND_UNKNOWN_PART_LABEL_SKU,
        );
        updateDraft(lineId, { unknown_mpn_quantity: String(nextCount), received_quantity: "" }, false);
      } else {
        const previous = countedQuantity(draft);
        const nextCount = nextReceivedAfterEntry(lineId, previous, quantity, false, line.sku);
        updateDraft(lineId, { received_quantity: String(nextCount), count_method: "manual" }, false);
      }
      speakReceiveSku(line.sku);
      speakReceiveQuantity(quantity);
      setLineQuery("");
      setScanFeedback(`Received ${quantity} of ${line.sku}.`);
      setSelectedLineId(null);
      setQuantityDraft("");
      returnToPickMode();
    },
    [nextReceivedAfterEntry, returnToPickMode, updateDraft],
  );

  const applyScannedMpn = useCallback(
    async (
      raw: string,
      lineIdOverride?: string,
      manufacturerName?: string | null,
      labelQuantity?: number | null,
    ): Promise<"matched" | "unmatched" | "choose" | "skip"> => {
      const lineId = lineIdOverride ?? selectedLineId;
      if (lineId == null || sourceType == null) return "skip";
      const line = stateRef.current.lines.find((row) => row.source_item_id === lineId) ?? null;
      if (line == null || !lineRequiresMpnEntry(line)) return "skip";
      const draft = stateRef.current.drafts[lineId] ?? EMPTY_LINE_DRAFT;
      const mpnState = resolveInboundMpnEntryState({ line, draft });
      if (mpnState === "matched" || mpnState === "unknown_confirmed") return "skip";
      const scanned = resolveGs1ManufacturerPartNumber(raw);
      if (scanned === "") return "skip";
      setMpnVerifying(true);
      try {
        const result = await verifyInboundLineMpn({
          source_type: sourceType,
          source_id: Number.parseInt(sourceId, 10),
          source_item_id: Number.parseInt(lineId, 10),
          scanned_mpn: scanned,
          manufacturer_name: manufacturerName ?? null,
        });
        if (result.status === "choose_manufacturer") {
          setManufacturerSeed(result.matched_mpn?.trim() || scanned);
          return "choose";
        }
        if (result.status === "matched" || result.status === "not_required") {
          const matchedMpn =
            result.status === "matched" && result.matched_mpn?.trim()
              ? result.matched_mpn.trim()
              : scanned;
          updateDraft(
            lineId,
            {
              scanned_mpn: matchedMpn,
              manufacturer_name: manufacturerName?.trim() ?? "",
              mpn_matched: true,
              approved_component_id:
                result.status === "matched" ? String(result.approved_component_id) : null,
              unknown_mpn_confirmed: false,
              unknown_mpn_quantity: "",
            },
            false,
          );
          playPairSuccessSound();
          if (labelQuantity != null) {
            applyLabelQuantityToLine(lineId, line, labelQuantity);
          }
          return "matched";
        }
        if (result.status === "unmatched") {
          updateDraft(
            lineId,
            {
              scanned_mpn: scanned,
              manufacturer_name: manufacturerName?.trim() ?? "",
              mpn_matched: false,
              approved_component_id: null,
              unknown_mpn_confirmed: false,
            },
            false,
          );
          playPairFailureSound();
          return "unmatched";
        }
      } catch (error) {
        playPairFailureSound();
        showToast({
          tone: "error",
          message: error instanceof ApiError ? error.message : String(error),
        });
        return "unmatched";
      } finally {
        setMpnVerifying(false);
      }
      return "skip";
    },
    [applyLabelQuantityToLine, selectedLineId, showToast, sourceId, sourceType, updateDraft],
  );

  const applyScannedSupplierLabel = useCallback(
    async (raw: string, allowPlainMpn = false): Promise<boolean> => {
      if (!inboundSupplierLabelMpnWasExtracted(raw)) {
        if (!allowPlainMpn) {
          return false;
        }
        const plainMpn = resolveInboundSupplierLabelMpn(raw);
        if (plainMpn.trim().length < 3) {
          return false;
        }
        try {
          const amlRows = await lookupInboundAmlByMpn(plainMpn);
          if (amlRows.length === 0) {
            return false;
          }
          const matchingLines = pickInboundOrderLinesForLabelScan(lines, amlRows);
          if (matchingLines.length === 0) {
            playPairFailureSound();
            const offOrderCpn = amlRows[0]?.cpn ?? "unknown CPN";
            setScanFeedback(`MPN ${plainMpn} maps to ${offOrderCpn}, which is not on this order.`);
            return true;
          }
          const dedupedMatches = collapseInboundLinesBySku(
            matchingLines
              .map((row) => lines.find((line) => line.source_item_id === row.source_item_id) ?? null)
              .filter((line): line is InboundLineRow => line != null),
          );
          if (dedupedMatches.length > 1) {
            playPairFailureSound();
            setScanFeedback(`MPN ${plainMpn} matches multiple lines on this order. Pick the line manually.`);
            return true;
          }
          const match = dedupedMatches[0]!;
          const line =
            canonicalInboundLineForSku(match.sku, lines)
            ?? lines.find((row) => row.source_item_id === match.source_item_id)
            ?? null;
          if (line == null) {
            return false;
          }
          if (lineRequiresMpnEntry(line)) {
            const currentDraft = stateRef.current.drafts[line.source_item_id] ?? EMPTY_LINE_DRAFT;
            const currentMpnState = resolveInboundMpnEntryState({ line, draft: currentDraft });
            if (!mpnEntryAllowsCounting(currentMpnState)) {
              const verified = await applyScannedMpn(plainMpn, line.source_item_id);
              if (verified === "skip") {
                return true;
              }
              if (verified === "choose") {
                setSelectedLineId(line.source_item_id);
                setScanFeedback(`Choose the manufacturer printed on the label for ${line.sku}.`);
                return true;
              }
              const draft = stateRef.current.drafts[line.source_item_id] ?? EMPTY_LINE_DRAFT;
              const mpnState = resolveInboundMpnEntryState({ line, draft });
              if (!mpnEntryAllowsCounting(mpnState)) {
                setSelectedLineId(line.source_item_id);
                setScanFeedback(`MPN ${plainMpn} does not match approved AML for ${line.sku}.`);
                return true;
              }
            }
          }
          playPairSuccessSound();
          speakReceiveSku(line.sku);
          selectLine(line.source_item_id);
          setScanFeedback(`Line ${line.sku} selected. Key quantity on the numpad.`);
          return true;
        } catch {
          return false;
        }
      }
      const mpn = resolveInboundSupplierLabelMpn(raw);
      const quantity = parseInboundSupplierLabelQuantity(raw);
      let amlRows;
      try {
        amlRows = await lookupInboundAmlByMpn(mpn);
      } catch (error) {
        playPairFailureSound();
        showToast({
          tone: "error",
          message: error instanceof ApiError ? error.message : String(error),
        });
        return true;
      }
      if (amlRows.length === 0) {
        playPairFailureSound();
        setScanFeedback(`MPN ${mpn} is not in approved AML.`);
        return true;
      }
      const matchingLines = pickInboundOrderLinesForLabelScan(lines, amlRows);
      if (matchingLines.length === 0) {
        playPairFailureSound();
        const offOrderCpn = amlRows[0]?.cpn ?? "unknown CPN";
        setScanFeedback(`MPN ${mpn} maps to ${offOrderCpn}, which is not on this order.`);
        return true;
      }
      const dedupedMatches = collapseInboundLinesBySku(
        matchingLines
          .map((row) => lines.find((line) => line.source_item_id === row.source_item_id) ?? null)
          .filter((line): line is InboundLineRow => line != null),
      );
      if (dedupedMatches.length > 1) {
        playPairFailureSound();
        setScanFeedback(`MPN ${mpn} matches multiple lines on this order. Pick the line manually.`);
        return true;
      }
      const match = dedupedMatches[0]!;
      const line =
        canonicalInboundLineForSku(match.sku, lines)
        ?? lines.find((row) => row.source_item_id === match.source_item_id)
        ?? null;
      if (line == null) {
        return false;
      }

      if (lineRequiresMpnEntry(line)) {
        const currentDraft = stateRef.current.drafts[line.source_item_id] ?? EMPTY_LINE_DRAFT;
        const currentMpnState = resolveInboundMpnEntryState({ line, draft: currentDraft });
        if (!mpnEntryAllowsCounting(currentMpnState)) {
          const verified = await applyScannedMpn(mpn, line.source_item_id);
          if (verified === "skip") {
            return true;
          }
          if (verified === "choose") {
            pendingSupplierLabelQuantityRef.current = quantity;
            setSelectedLineId(line.source_item_id);
            setScanFeedback(`Choose the manufacturer printed on the label for ${line.sku}.`);
            return true;
          }
          const draft = stateRef.current.drafts[line.source_item_id] ?? EMPTY_LINE_DRAFT;
          const mpnState = resolveInboundMpnEntryState({ line, draft });
          if (!mpnEntryAllowsCounting(mpnState)) {
            setSelectedLineId(line.source_item_id);
            setScanFeedback(`MPN ${mpn} does not match approved AML for ${line.sku}.`);
            return true;
          }
        }
      }

      if (quantity != null) {
        applyLabelQuantityToLine(line.source_item_id, line, quantity);
        return true;
      }

      playPairSuccessSound();
      speakReceiveSku(line.sku);
      selectLine(line.source_item_id);
      setScanFeedback(`Line ${line.sku} selected. Key quantity on the numpad.`);
      return true;
    },
    [
      applyLabelQuantityToLine,
      applyScannedMpn,
      lines,
      selectLine,
      showToast,
    ],
  );

  const submitQuantity = useCallback(() => {
    if (selectedLineId == null) return;
    const eachError =
      openSubcontainerUnitKind === "each"
        ? inboundEachQuantityError(Number.parseInt(quantityDraft, 10))
        : null;
    if (eachError != null && quantityDraft !== "") {
      playPairFailureSound();
      showToast({ message: eachError, tone: "warning" });
      return;
    }
    const line = stateRef.current.lines.find((row) => row.source_item_id === selectedLineId) ?? null;
    const draft = stateRef.current.drafts[selectedLineId] ?? EMPTY_LINE_DRAFT;
    const mpnState =
      line == null ? "not_required" : resolveInboundMpnEntryState({ line, draft });
    if (!mpnEntryAllowsCounting(mpnState)) {
      playPairFailureSound();
      showToast({
        message: "Enter the manufacturer part number before counting.",
        tone: "warning",
      });
      return;
    }
    const entered = Number.parseInt(quantityDraft, 10);
    if (!Number.isFinite(entered) || quantityDraft === "") return;
    const unknownPath = countingUnknownMpnPath(mpnState);
    if (unknownPath) {
      const previous = unknownCountedQuantity(draft);
      const nextCount = nextReceivedAfterEntry(
        selectedLineId,
        previous,
        entered,
        editingReplace,
        INBOUND_UNKNOWN_PART_LABEL_SKU,
      );
      updateDraft(selectedLineId, { unknown_mpn_quantity: String(nextCount), received_quantity: "" }, false);
    } else {
      const previous = countedQuantity(draft);
      const nextCount = nextReceivedAfterEntry(
        selectedLineId,
        previous,
        entered,
        editingReplace,
        line?.sku ?? "",
      );
      updateDraft(selectedLineId, { received_quantity: String(nextCount), count_method: "manual" }, false);
    }
    speakReceiveQuantity(entered);
    setQuantityDraft("");
    setSelectedLineId(null);
    returnToPickMode();
  }, [
    editingReplace,
    nextReceivedAfterEntry,
    openSubcontainerUnitKind,
    quantityDraft,
    returnToPickMode,
    selectedLineId,
    showToast,
    updateDraft,
  ]);

  const applyScannedQuantity = useCallback(
    async (raw: string): Promise<boolean> => {
      if (selectedLineId == null) return false;
      const line = stateRef.current.lines.find((row) => row.source_item_id === selectedLineId) ?? null;
      const draft = stateRef.current.drafts[selectedLineId] ?? EMPTY_LINE_DRAFT;
      const mpnState =
        line == null ? "not_required" : resolveInboundMpnEntryState({ line, draft });
      if (!mpnEntryAllowsCounting(mpnState)) {
        if (parseReceiveQuantityScan(raw) != null) {
          playPairFailureSound();
          showToast({
            message: "Enter the manufacturer part number before counting.",
            tone: "warning",
          });
          return true;
        }
        return false;
      }
      const entered = parseReceiveQuantityScan(raw);
      if (entered == null) return false;
      const unknownPath = countingUnknownMpnPath(mpnState);
      if (unknownPath) {
        const previous = unknownCountedQuantity(draft);
        const nextCount = nextReceivedAfterEntry(
          selectedLineId,
          previous,
          entered,
          editingReplace,
          INBOUND_UNKNOWN_PART_LABEL_SKU,
        );
        updateDraft(selectedLineId, { unknown_mpn_quantity: String(nextCount), received_quantity: "" }, false);
      } else {
        const previous = countedQuantity(draft);
        const nextCount = nextReceivedAfterEntry(
          selectedLineId,
          previous,
          entered,
          editingReplace,
          line?.sku ?? "",
        );
        updateDraft(selectedLineId, { received_quantity: String(nextCount), count_method: "manual" }, false);
      }
      speakReceiveQuantity(entered);
      setQuantityDraft("");
      setLineQuery("");
      setSelectedLineId(null);
      returnToPickMode();
      return true;
    },
    [editingReplace, nextReceivedAfterEntry, returnToPickMode, selectedLineId, showToast, updateDraft],
  );

  const handleScan = useCallback(
    async (scanned: string) => {
      if (await applyScannedSupplierLabel(scanned)) return;
      if (await applyScannedQuantity(scanned)) return;
      if (looksLikeArrivalPackageScan(scanned)) {
        const handled = await processArrivalPackageScan(scanned);
        if (handled) return;
      }
      const needle = scanned.trim().toLowerCase();
      if (needle === "") return;
      setLineQuery("");
      const match =
        canonicalInboundLineForSku(scanned.trim(), lines)
        ?? lines.find((line) => line.sku.trim().toLowerCase() === needle);
      if (match == null) {
        if (await applyScannedSupplierLabel(scanned, true)) return;
        playPairFailureSound();
        setUnlistedSkuPrefill(scanned.trim());
        setScanFeedback(
          `"${scanned.trim()}" is not on this order. Add it as an unlisted SKU or pick a line below.`,
        );
        return;
      }
      playPairSuccessSound();
      speakReceiveSku(match.sku);
      setScanFeedback(null);
      selectLine(resolveInboundReceiveLineId(match, lines));
    },
    [applyScannedSupplierLabel, lines, processArrivalPackageScan, selectLine],
  );

  useFloorPageScanCapture({
    enabled: !receiveTypingModalOpen,
    autoFocus: !receiveTypingModalOpen,
    captureUnfocusedHid:
      !receiptReadOnly
      && !receiveTypingModalOpen
      && (inputMode === "scan" || (inputMode === "quantity" && selectedLineId != null)),
    inputRef: scanInputRef,
    onScan: (value) => {
      if (receiptReadOnly) return;
      void (async () => {
        if (await applyScannedQuantity(value)) return;
        await handleScan(value);
      })();
    },
  });

  const startEditingCount = useCallback(
    (lineId: string) => {
      const currentLines = stateRef.current.lines;
      const line = currentLines.find((row) => row.source_item_id === lineId);
      setSelectedLineId(line != null ? resolveInboundReceiveLineId(line, currentLines) : lineId);
      setEditingReplace(true);
      setQuantityDraft("");
      setScanFeedback(null);
      setInputMode("quantity");
    },
    [],
  );

  const clearCount = useCallback(
    async (lineId: string) => {
      const currentLines = stateRef.current.lines;
      const line = currentLines.find((row) => row.source_item_id === lineId);
      const lineIds =
        line != null && isOrderLine(line) ? inboundLineIdsForSku(line.sku, currentLines) : [lineId];
      const closed = lineIds.reduce((sum, id) => sum + closedQuantityForLine(id, closedItems), 0);
      const closedUnknown = lineIds.reduce(
        (sum, id) => sum + closedQuantityForLineSku(id, INBOUND_UNKNOWN_PART_LABEL_SKU, closedItems),
        0,
      );
      const closedKnown = Math.max(0, closed - closedUnknown);
      const previousDraft = stateRef.current.drafts[lineId] ?? EMPTY_LINE_DRAFT;
      const clearedDraft = {
        ...previousDraft,
        received_quantity: closedKnown > 0 ? String(closedKnown) : "",
        unknown_mpn_quantity: closedUnknown > 0 ? String(closedUnknown) : "",
        ...(closedKnown > 0 || closedUnknown > 0 ? {} : emptyInboundLineMpnDraftFields()),
      };
      setDrafts((current) => {
        const next: Record<string, LineDraft> = {
          ...current,
          [lineId]: clearedDraft,
        };
        for (const siblingId of lineIds) {
          if (siblingId === lineId) continue;
          const siblingDraft = current[siblingId];
          if (siblingDraft == null) continue;
          next[siblingId] = {
            ...siblingDraft,
            received_quantity: "",
            unknown_mpn_quantity: "",
            ...(closedKnown > 0 || closedUnknown > 0 ? {} : emptyInboundLineMpnDraftFields()),
          };
        }
        stateRef.current = {
          ...stateRef.current,
          drafts: next,
        };
        return next;
      });
      if (selectedLineId === lineId) {
        setSelectedLineId(null);
        setQuantityDraft("");
        returnToPickMode();
      }
    },
    [closedItems, returnToPickMode, selectedLineId],
  );

  const ensureDraftReceiptForExceptions = useCallback(async (): Promise<InboundReceipt> => {
    if (sourceType == null || sourceId === "") {
      throw new Error("Invalid inbound source.");
    }
    const current = stateRef.current;
    const saved =
      activeReceipt
      ?? (await ensureInboundDraftReceipt(
        buildReceiptSaveInput({
          sourceType,
          sourceId,
          arrivalPackageId: activeArrivalPackageId ?? null,
          notes: current.notes,
          lines: current.lines,
          drafts: current.drafts,
          closedItems,
          retainEmptyDraft: true,
        }),
      ));
    if (saved == null) {
      throw new Error("Could not open a draft receipt for this crate.");
    }
    return saved;
  }, [activeArrivalPackageId, activeReceipt, sourceId, sourceType]);

  const handleDamagedSplit = useCallback(
    async (input: { goodQuantity: number; damagedQuantity: number; notes: string }) => {
      if (selectedLineId == null) return;
      const line = stateRef.current.lines.find((row) => row.source_item_id === selectedLineId);
      if (line == null || !isOrderLine(line)) return;
      setExceptionPending(true);
      try {
        const receipt = await ensureDraftReceiptForExceptions();
        await splitInboundDamagedOrderLine(receipt.id, {
          source_item_id: Number(line.source_item_id),
          good_quantity: input.goodQuantity,
          damaged_quantity: input.damagedQuantity,
          notes: input.notes.trim() === "" ? null : input.notes.trim(),
        });
        containerLaborWriteRef.current?.();
        updateDraft(selectedLineId, { received_quantity: String(input.goodQuantity) }, false);
        await flushDraftSave([selectedLineId]);
        setDamagedModalOpen(false);
        showToast({ tone: "success", message: "Damaged quantity flagged on this line." });
        void queryClient.invalidateQueries({
          queryKey: ["shipping", "inbound", sourceType, sourceId, activeArrivalPackageId ?? ""],
        });
      } catch (error) {
        showToast({ message: error instanceof ApiError ? error.message : String(error), tone: "error" });
      } finally {
        setExceptionPending(false);
      }
    },
    [
      activeArrivalPackageId,
      ensureDraftReceiptForExceptions,
      flushDraftSave,
      queryClient,
      selectedLineId,
      showToast,
      sourceId,
      sourceType,
      updateDraft,
    ],
  );

  const handleAddUnlistedSku = useCallback(
    async (input: { sku: string }) => {
      setExceptionPending(true);
      try {
        const receipt = await ensureDraftReceiptForExceptions();
        const result = await createInboundAdHocReceiptLine(receipt.id, {
          line_kind: "unlisted_sku",
          sku: input.sku,
          received_quantity: 0,
        });
        containerLaborWriteRef.current?.();
        seededSourceRef.current = null;
        setUnlistedModalOpen(false);
        setScanFeedback(null);
        setUnlistedSkuPrefill("");
        selectLine(result.line.source_item_id);
        setInputMode("quantity");
        showToast({
          tone: "success",
          message: `Added ${input.sku}. Key the quantity on the numpad.`,
        });
        void queryClient.invalidateQueries({
          queryKey: ["shipping", "inbound", sourceType, sourceId, activeArrivalPackageId ?? ""],
        });
      } catch (error) {
        showToast({ message: error instanceof ApiError ? error.message : String(error), tone: "error" });
      } finally {
        setExceptionPending(false);
      }
    },
    [
      activeArrivalPackageId,
      ensureDraftReceiptForExceptions,
      queryClient,
      selectLine,
      showToast,
      sourceId,
      sourceType,
    ],
  );

  const handleAddNonSkuItem = useCallback(
    async (input: { description: string; quantity: number }) => {
      setExceptionPending(true);
      try {
        const receipt = await ensureDraftReceiptForExceptions();
        await createInboundAdHocReceiptLine(receipt.id, {
          line_kind: "non_sku",
          description: input.description,
          received_quantity: input.quantity,
        });
        containerLaborWriteRef.current?.();
        seededSourceRef.current = null;
        setNonSkuModalOpen(false);
        showToast({ tone: "success", message: "Added non-SKU item." });
        void queryClient.invalidateQueries({
          queryKey: ["shipping", "inbound", sourceType, sourceId, activeArrivalPackageId ?? ""],
        });
      } catch (error) {
        showToast({ message: error instanceof ApiError ? error.message : String(error), tone: "error" });
      } finally {
        setExceptionPending(false);
      }
    },
    [activeArrivalPackageId, ensureDraftReceiptForExceptions, queryClient, showToast, sourceId, sourceType],
  );

  /* ---- submit + packing slip --------------------------------------------------- */

  const finishArrivalMutation = useMutation({
    mutationFn: async () => {
      if (!hasPackingSlip) throw new Error("Upload a packing slip before finishing this arrival container.");
      if (saveTimerRef.current != null) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      const openLineIds = openSubcontainerLines({
        lines: stateRef.current.lines.map((line) => {
          const draft = stateRef.current.drafts[line.source_item_id];
          return {
            source_item_id: line.source_item_id,
            sku: line.sku,
            received_quantity: countedQuantity(draft),
            unknown_mpn_quantity: unknownCountedQuantity(draft),
            scanned_mpn: draft?.scanned_mpn ?? null,
          };
        }),
        closedItems,
      }).map((openLine) => openLine.source_item_id);
      const saved = await flushDraftSave(openLineIds);
      const receiptId = saved?.id ?? activeReceiptIdRef.current;
      if (receiptId == null) {
        throw new Error("Could not save the draft receipt before finishing.");
      }
      if (saved != null && inboundReceiptPackingSlips(saved).length === 0) {
        throw new Error("Upload a packing slip before finishing this arrival container.");
      }
      return finishInboundReceiptArrivalContainer(receiptId);
    },
    onSuccess: (result) => {
      const goesToHolding =
        result.holding_tasks_created > 0 || (result.put_away_routing?.holding_line_count ?? 0) > 0;
      const destination = goesToHolding ? "Holding" : "To Stock";
      if (goesToHolding) {
        playLaborIdleWarningSound();
        showToast({
          message: (
            <>
              Arrival container finished. Send arrival to <strong>{destination}</strong>.
            </>
          ),
          tone: "warning",
        });
      } else {
        playPairSuccessSound();
        showToast({
          message: (
            <>
              Arrival container finished. Send arrival to <strong>{destination}</strong>.
            </>
          ),
          tone: "success",
        });
      }
      seededSourceRef.current = null;
      void queryClient.invalidateQueries({ queryKey: ["shipping", "inbound"] });
      navigate("/shipping/receive", { replace: true });
    },
    onError: (error) => {
      playPairFailureSound();
      showToast({ message: error instanceof ApiError ? error.message : String(error), tone: "error" });
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (saveTimerRef.current != null) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      const current = stateRef.current;
      if (sourceType == null || sourceId === "") throw new Error("Invalid inbound source.");
      let receiptId = activeReceipt?.id;
      if (receiptId == null) {
        const saved = await ensureInboundDraftReceipt(
          buildReceiptSaveInput({
            sourceType,
            sourceId,
            arrivalPackageId: activeArrivalPackageId ?? null,
            notes: current.notes,
            lines: current.lines,
            drafts: current.drafts,
            closedItems,
            retainEmptyDraft: true,
          }),
        );
        receiptId = saved.id;
      }
      return uploadInboundPackingSlip(receiptId, file);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["shipping", "inbound", sourceType, sourceId] });

      const queued = pendingPackingSlipQueueRef.current;
      if (queued.length > 0) {
        const next = queued.shift()!;
        setPendingPackingSlipFile(next.file);
        setPendingPackingSlipPageLabel(next.pageLabel);
        setPackingSlipConfirmOpen(true);
        showToast({ message: "Packing slip page saved. Review the next upload.", tone: "success" });
        return;
      }

      if (packingSlipAddAnotherAfterSaveRef.current) {
        packingSlipAddAnotherAfterSaveRef.current = false;
        showToast({ message: "Packing slip page saved.", tone: "success" });
        return;
      }

      showToast({ message: "Packing slip page saved.", tone: "success" });
    },
    onError: (error) =>
      showToast({ message: error instanceof ApiError ? error.message : String(error), tone: "error" }),
  });

  const packingSlipDeleteMutation = useMutation({
    mutationFn: ({ receiptId, slipId }: { receiptId: string; slipId: string }) =>
      deleteInboundPackingSlip(receiptId, slipId),
    onSuccess: () => {
      setPackingSlipMenuOpen(false);
      showToast({ message: "Packing slip removed.", tone: "success" });
      void queryClient.invalidateQueries({ queryKey: ["shipping", "inbound"], exact: true });
      void queryClient.invalidateQueries({ queryKey: ["shipping", "inbound", sourceType, sourceId] });
    },
    onError: (error) =>
      showToast({ message: error instanceof ApiError ? error.message : String(error), tone: "error" }),
  });

  const warnIfPrinterNotFourByTwo = useCallback(() => {
    const selectedPrinterRow = printersQuery.data?.printers.find(
      (printerRow) => printerRow.name === selectedZplPrinter,
    );
    const selectedStockSize = selectedPrinterRow?.labelSize?.trim();
    if (selectedStockSize != null && selectedStockSize !== "" && selectedStockSize !== "4x2") {
      showToast({
        tone: "info",
        message: `Selected printer is ${selectedStockSize} stock; inbound inventory labels are 4x2.`,
      });
    }
  }, [printersQuery.data?.printers, selectedZplPrinter, showToast]);

  const handleOpenSubcontainerUnitKindChange = useCallback(
    async (kind: InboundArrivalUnitKind) => {
      if (sourceType == null || sourceId === "") return;
      setPendingOpenSubcontainerUnitKind(kind);
      try {
        let receiptId = activeReceipt?.id;
        if (receiptId == null) {
          const current = stateRef.current;
          const saved = await ensureInboundDraftReceipt(
            buildReceiptSaveInput({
              sourceType,
              sourceId,
              arrivalPackageId: activeArrivalPackageId,
              notes: current.notes,
              lines: current.lines,
              drafts: current.drafts,
              closedItems,
              retainEmptyDraft: true,
            }),
          );
          receiptId = saved.id;
        }
        await patchInboundReceiptOpenSubcontainerUnitKind(receiptId, kind);
        containerLaborWriteRef.current?.();
        void queryClient.invalidateQueries({
          queryKey: ["shipping", "inbound", sourceType, sourceId, activeArrivalPackageId ?? ""],
        });
      } catch (error) {
        setPendingOpenSubcontainerUnitKind(null);
        if (!isLaborOwnerDeparting()) {
          showToast({ message: error instanceof ApiError ? error.message : String(error), tone: "error" });
        }
      }
    },
    [activeArrivalPackageId, activeReceipt?.id, queryClient, showToast, sourceId, sourceType],
  );

  const handleSealedSubcontainerUnitKindChange = useCallback(
    async (subcontainerId: string, kind: InboundArrivalUnitKind) => {
      const receiptId = activeReceipt?.id;
      if (receiptId == null) {
        showToast({ tone: "error", message: "Could not update container type without an open receipt." });
        return;
      }
      setSealedUnitKindPendingId(subcontainerId);
      try {
        await patchInboundSubcontainerUnitKind(receiptId, subcontainerId, kind);
        void queryClient.invalidateQueries({ queryKey: ["shipping", "inbound", sourceType, sourceId] });
      } catch (error) {
        showToast({ message: error instanceof ApiError ? error.message : String(error), tone: "error" });
      } finally {
        setSealedUnitKindPendingId(null);
      }
    },
    [activeReceipt?.id, queryClient, showToast, sourceId, sourceType],
  );

  const handleUnsealSubcontainer = useCallback(
    async (subcontainerId: string) => {
      const receiptId = activeReceipt?.id;
      if (receiptId == null) {
        showToast({ tone: "error", message: "Could not unseal without an open receipt." });
        return;
      }
      if (openContainerLines.length > 0) {
        showToast({ tone: "warning", message: RECEIVE_OPEN_CONTAINER_BLOCK_MESSAGE });
        return;
      }
      const sealed = subcontainers.find((row) => row.id === subcontainerId);
      const heading =
        sealed != null
          ? formatInboundSubcontainerHeading(
              parseInboundSubcontainerUnitKind(sealed.unit_kind),
              sealed.subcontainer_no,
            )
          : "Container";
      setUnsealPendingId(subcontainerId);
      try {
        const result = await unsealInboundSubcontainer(receiptId, subcontainerId);
        const labelsVoided = result.labels_voided ?? 0;
        showToast({
          tone: "success",
          message:
            labelsVoided > 0
              ? `${heading} unsealed. ${
                  labelsVoided === 1 ? "That container label is voided." : "Those container labels are voided."
                } Seal again to print a new label.`
              : `${heading} unsealed. Correct its quantity, then seal it again.`,
        });
        if (result.receipt != null) {
          void queryClient.invalidateQueries({
            queryKey: ["shipping", "inbound", sourceType, sourceId, activeArrivalPackageId ?? ""],
          });
        } else {
          void queryClient.invalidateQueries({ queryKey: ["shipping", "inbound", sourceType, sourceId] });
        }
      } catch (error) {
        showToast({ message: error instanceof ApiError ? error.message : String(error), tone: "error" });
      } finally {
        setUnsealPendingId(null);
      }
    },
    [
      activeArrivalPackageId,
      activeReceipt?.id,
      openContainerLines.length,
      queryClient,
      showToast,
      sourceId,
      sourceType,
      subcontainers,
    ],
  );

  const closeSubcontainerMutation = useMutation({
    mutationFn: async () => {
      if (saveTimerRef.current != null) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      const expectedOpen = openSubcontainerLines({
        lines: inboundOpenSubcontainerInputLines(
          stateRef.current.lines,
          stateRef.current.drafts,
          closedItems,
        ),
        closedItems,
      });
      if (expectedOpen.length === 0) {
        throw new Error("Count quantity onto a line before sealing.");
      }
      const printLines = expectedOpen.map((openLine) => {
        const line = stateRef.current.lines.find((row) => row.source_item_id === openLine.source_item_id);
        const draft = stateRef.current.drafts[openLine.source_item_id];
        const mpnDraft =
          line != null
            ? resolveInboundOrderLineMpnDraft(line, stateRef.current.lines, stateRef.current.drafts)
            : null;
        return {
          sku: openLine.sku,
          quantity: openLine.quantity,
          description:
            openLine.label_description
            ?? (mpnDraft?.scanned_mpn.trim() || null)
            ?? draft?.scanned_mpn
            ?? line?.description
            ?? null,
          source_item_id: openLine.source_item_id,
          inbound_receipt_item_id: line?.receipt_item_id ?? null,
        };
      });
      const saved = await flushDraftSave(expectedOpen.map((openLine) => openLine.source_item_id));
      if (saved == null && activeReceiptIdRef.current == null) {
        throw new Error("Could not save counts before sealing. Fix any save errors and try again.");
      }
      const receiptId = saved?.id ?? activeReceiptIdRef.current;
      if (receiptId == null) {
        throw new Error("Could not save counts before sealing. Fix any save errors and try again.");
      }
      if (openSubcontainerUnitKind == null) {
        throw new Error("Pick a container type before sealing.");
      }
      const printer = selectedZplPrinter.trim();
      const closeResult = await closeInboundSubcontainer(receiptId, openSubcontainerUnitKind, {
        printer: printer !== "" ? printer : undefined,
        printLines: printer !== "" ? printLines : undefined,
      });
      return { closeResult, printer };
    },
    onSuccess: async ({ closeResult, printer }) => {
      const sealedHeading = formatInboundSubcontainerHeading(
        parseInboundSubcontainerUnitKind(closeResult.subcontainer.unit_kind),
        closeResult.subcontainer.subcontainer_no,
      );
      const batch = closeResult.print;
      if (printer !== "" && batch != null && batch.failed > 0) {
        playPairFailureSound();
        const firstFail = batch.results.find((row) => !row.ok);
        showToast({
          tone: "error",
          message: `${sealedHeading} sealed but ${formatNumber(
            batch.failed,
          )} label print failed${firstFail ? ` - ${firstFail.sku}: ${firstFail.error ?? "print error"}` : ""}.`,
        });
      } else {
        playPairSuccessSound();
        if (printer !== "" && batch != null) {
          showToast({
            tone: "success",
            message: `${sealedHeading} sealed. ${formatNumber(batch.printed)} label${
              batch.printed === 1 ? "" : "s"
            } sent to printer.`,
          });
        } else {
          showToast({
            tone: "success",
            message: `${sealedHeading} sealed.`,
          });
        }
      }
      setPendingOpenSubcontainerUnitKind(null);
      setSelectedLineId(null);
      setQuantityDraft("");
      returnToPickMode();
      void queryClient.invalidateQueries({ queryKey: ["shipping", "inbound", sourceType, sourceId] });
    },
    onError: (error) => {
      playPairFailureSound();
      showToast({ message: error instanceof ApiError ? error.message : String(error), tone: "error" });
    },
  });

  const handleCloseAndPrint = useCallback(() => {
    if (!sealReady) return;
    warnIfPrinterNotFourByTwo();
    closeSubcontainerMutation.mutate();
  }, [closeSubcontainerMutation, sealReady, warnIfPrinterNotFourByTwo]);

  const handleReprintSubcontainer = useCallback(
    async (subcontainerNo: number, items: InboundReceipt["subcontainers"][number]["items"]) => {
      if (selectedZplPrinter.trim() === "") {
        showToast({ tone: "error", message: "Select a label printer first." });
        return;
      }
      warnIfPrinterNotFourByTwo();
      setPrintingSubcontainerNo(subcontainerNo);
      const sealed = subcontainers.find((row) => row.subcontainer_no === subcontainerNo);
      const sealedHeading =
        sealed != null
          ? formatInboundSubcontainerHeading(parseInboundSubcontainerUnitKind(sealed.unit_kind), subcontainerNo)
          : `Container #${String(subcontainerNo)}`;
      try {
        const batch = await printInboundInventoryLabelBatch({
          printer: selectedZplPrinter,
          lines: items.map((item) => {
            const line = lines.find((row) => row.source_item_id === item.source_item_id);
            return {
              sku: item.sku,
              quantity: Number.parseFloat(item.quantity) || 0,
              description: line?.description ?? null,
              source_item_id: item.source_item_id,
              inbound_receipt_item_id: line?.receipt_item_id ?? null,
            };
          }),
        });
        if (batch.failed === 0) {
          playPairSuccessSound();
          showToast({
            tone: "success",
            message: `${formatNumber(batch.printed)} label${batch.printed === 1 ? "" : "s"} reprinted for ${sealedHeading}.`,
          });
        } else {
          playPairFailureSound();
          const firstFail = batch.results.find((row) => !row.ok);
          showToast({
            tone: "error",
            message: `Reprint failed for ${formatNumber(batch.failed)} label${
              batch.failed === 1 ? "" : "s"
            }${firstFail ? ` - ${firstFail.sku}: ${firstFail.error ?? "print error"}` : ""}.`,
          });
        }
      } catch (error) {
        playPairFailureSound();
        showToast({
          message: error instanceof ApiError ? error.message : String(error),
          tone: "error",
        });
      } finally {
        setPrintingSubcontainerNo(null);
      }
    },
    [lines, selectedZplPrinter, showToast, subcontainers, warnIfPrinterNotFourByTwo],
  );

  const clearPackingSlipConfirmState = useCallback(() => {
    setPendingPackingSlipFile(null);
    setPendingPackingSlipPageLabel(null);
    setPackingSlipConfirmOpen(false);
    pendingPackingSlipQueueRef.current = [];
    packingSlipAddAnotherAfterSaveRef.current = false;
  }, []);

  const queuePackingSlipForConfirm = useCallback((file: File, pageLabel?: string) => {
    setPendingPackingSlipFile(file);
    setPendingPackingSlipPageLabel(pageLabel ?? null);
    setPackingSlipConfirmOpen(true);
  }, []);

  const queuePackingSlipFilesForConfirm = useCallback(
    (files: readonly File[]) => {
      if (files.length === 0) return;
      const labels = files.map((_, index) => `Page ${String(index + 1)} of ${String(files.length)}`);
      pendingPackingSlipQueueRef.current = files.slice(1).map((file, index) => ({
        file,
        pageLabel: labels[index + 1],
      }));
      queuePackingSlipForConfirm(files[0], labels[0]);
    },
    [queuePackingSlipForConfirm],
  );

  const handlePackingSlipFilePick = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      event.target.value = "";
      if (files.length === 0) return;
      if (files.length === 1) {
        queuePackingSlipForConfirm(files[0], `Page ${String(packingSlipCount + 1)}`);
        return;
      }
      queuePackingSlipFilesForConfirm(files);
    },
    [packingSlipCount, queuePackingSlipFilesForConfirm, queuePackingSlipForConfirm],
  );

  const handleConfirmPackingSlip = useCallback(
    (addAnother: boolean) => {
      if (pendingPackingSlipFile == null) return;
      const file = pendingPackingSlipFile;
      packingSlipAddAnotherAfterSaveRef.current = addAnother;
      setPendingPackingSlipFile(null);
      setPendingPackingSlipPageLabel(null);
      setPackingSlipConfirmOpen(false);
      if (addAnother) {
        setPackingSlipCameraOpen(true);
      }
      uploadMutation.mutate(file);
    },
    [pendingPackingSlipFile, uploadMutation],
  );

  const handleCancelPackingSlipConfirm = useCallback(() => {
    clearPackingSlipConfirmState();
  }, [clearPackingSlipConfirmState]);

  const handleTryAgainPackingSlipConfirm = useCallback(() => {
    clearPackingSlipConfirmState();
    setPackingSlipCameraOpen(true);
  }, [clearPackingSlipConfirmState]);

  const handleViewPackingSlip = useCallback(
    (slipId: string) => {
      if (activeReceipt == null) return;
      setPackingSlipMenuOpen(false);
      void fetchPackingSlipUrl(activeReceipt.id, slipId)
        .then((result) => openDocumentUrl(result.signedUrl))
        .catch((error) => {
          showToast({
            message: error instanceof ApiError ? error.message : "Could not open packing slip.",
            tone: "error",
          });
        });
    },
    [activeReceipt, showToast],
  );

  const handleArrivalOrderPick = useCallback(
    (source: InboundArrivalOrderPickSource) => {
      if (arrivalOrderPick == null) return;
      const target = buildInboundReceiveNavigateTarget(source.source_type, source.source_id, {
        arrivalPackageId: arrivalOrderPick.packageId,
        arrivalPackageNo: arrivalOrderPick.packageNo,
        arrivalScan: arrivalOrderPick.scan,
        arrivalUnitKind: parseInboundArrivalUnitKind(arrivalOrderPick.unitKind),
      });
      setArrivalOrderPick(null);
      navigate({ pathname: target.pathname, search: target.search }, { state: target.state });
    },
    [arrivalOrderPick, navigate],
  );

  const handleDeletePackingSlip = useCallback(
    (slipId: string) => {
      if (activeReceipt == null) return;
      if (slipId === "legacy") {
        void deleteAllInboundPackingSlips(activeReceipt.id).then(() => {
          setPackingSlipMenuOpen(false);
          showToast({ message: "Packing slip removed.", tone: "success" });
          void queryClient.invalidateQueries({ queryKey: ["shipping", "inbound", sourceType, sourceId] });
        });
        return;
      }
      packingSlipDeleteMutation.mutate({ receiptId: activeReceipt.id, slipId });
    },
    [activeReceipt, packingSlipDeleteMutation, queryClient, showToast, sourceId, sourceType],
  );

  /* ---- kiosk chrome ------------------------------------------------------------ */

  useEffect(() => {
    if (!packingSlipMenuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (packingSlipMenuRef.current?.contains(target)) return;
      setPackingSlipMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [packingSlipMenuOpen]);

  useEffect(() => {
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overflow = previousBodyOverflow;
    };
  }, []);

  useEffect(() => {
    primeReceiveFloorSpeechSynthesis();
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(PREPACK_TRANSFER_NUMPAD_SIDE_STORAGE_KEY, numpadSide);
    } catch {
      /* ignore */
    }
  }, [numpadSide]);

  useEffect(() => {
    if (inputMode !== "quantity" || selectedLineId == null || receiveTypingModalOpen) {
      quantityScanBufferRef.current = "";
      quantityAltNumpadRef.current = "";
      return;
    }
    const applyPlan = (key: string): boolean => {
      const plan = applyReceiveQuantityModeKey(quantityScanBufferRef.current, key);
      quantityScanBufferRef.current = plan.type === "submit-scan" ? "" : plan.buffer;
      if (plan.type === "ignore") return false;
      if (plan.type === "quantity-digit") {
        appendDigit(plan.digit);
        return true;
      }
      if (plan.type === "quantity-backspace") {
        setQuantityDraft((current) => current.slice(0, -1));
        return true;
      }
      if (plan.type === "hold-scan") {
        if (plan.rewindDigits > 0) {
          const rewind = plan.rewindDigits;
          setQuantityDraft((current) => current.slice(0, Math.max(0, current.length - rewind)));
        }
        return true;
      }
      if (plan.type === "submit-scan") {
        setLineQuery("");
        void handleScan(plan.scanned);
        return true;
      }
      submitQuantity();
      return true;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey) return;
      const noted = appendAltNumpadDigit(quantityAltNumpadRef.current, event.code, event.altKey);
      if (noted.consumed) {
        quantityAltNumpadRef.current = noted.buffer;
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (event.altKey || event.defaultPrevented) return;
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const inNumpad = target.closest("[data-transfer-pack-numpad]") != null;
      const blocksKeys =
        target !== scanInputRef.current
        && (target.closest("input, textarea, select") != null
          || (target.closest("button, a") != null && !inNumpad));
      if (blocksKeys) return;
      if (!applyPlan(event.key)) return;
      event.preventDefault();
      event.stopPropagation();
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key !== "Alt") return;
      const separator = characterForCompletedAltNumpad(quantityAltNumpadRef.current);
      quantityAltNumpadRef.current = "";
      if (separator == null) return;
      event.preventDefault();
      event.stopPropagation();
      applyPlan(separator);
    };
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("keyup", onKeyUp, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("keyup", onKeyUp, true);
    };
  }, [appendDigit, handleScan, inputMode, receiveTypingModalOpen, selectedLineId, submitQuantity]);

  /* ---- render ------------------------------------------------------------------ */

  if (sourceType == null) {
    return <p className="py-8 text-center text-sm text-rose-700">Invalid inbound source.</p>;
  }

  if (detailQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading receive workspace...
      </div>
    );
  }

  if (detailQuery.isError || detail == null) {
    return (
      <p className="py-8 text-center text-sm text-rose-700">
        {detailQuery.error instanceof ApiError ? detailQuery.error.message : "Inbound order not found."}
      </p>
    );
  }

  const selectedLine = selectedLineId != null ? lines.find((line) => line.source_item_id === selectedLineId) ?? null : null;
  const selectedLineDraft = selectedLineId != null ? drafts[selectedLineId] ?? EMPTY_LINE_DRAFT : EMPTY_LINE_DRAFT;
  const selectedLineMpnState =
    selectedLine == null
      ? "not_required"
      : resolveInboundMpnEntryState({ line: selectedLine, draft: selectedLineDraft });
  const countedLines = lines.filter((line) => countedQuantity(drafts[line.source_item_id]) > 0);
  const countedTotal = countedLines.reduce((sum, line) => sum + countedQuantity(drafts[line.source_item_id]), 0);
  const finishArrivalBlockReason = inboundReceiveFinishArrivalBlockReason({
    receiptReadOnly,
    hasScannedArrivalPackage,
    openContainerCount,
    countedTotal,
    hasPackingSlip,
  });
  const numpadOnRight = numpadSide === "right";
  const labeledContainerCount = subcontainers.length;
  const openQuantityForLine = (lineId: string): number => {
    const openLine = openContainerLines.find((row) => row.source_item_id === lineId);
    return openLine?.quantity ?? 0;
  };
  const openQuantityForDisplayLine = (line: InboundLineRow): number => {
    const lineIds = isOrderLine(line) ? inboundLineIdsForSku(line.sku, lines) : [line.source_item_id];
    return lineIds.reduce((sum, lineId) => sum + openQuantityForLine(lineId), 0);
  };
  const closedQuantityForDisplayLine = (line: InboundLineRow): number => {
    const lineIds = isOrderLine(line) ? inboundLineIdsForSku(line.sku, lines) : [line.source_item_id];
    return lineIds.reduce((sum, lineId) => sum + closedQuantityForLine(lineId, closedItems), 0);
  };

  return (
    <InboundContainerLaborProvider
      arrivalPackageId={activeArrivalPackageId}
      wirePage={receiveLaborPage}
      workLabel="Receive"
      writeRef={containerLaborWriteRef}
    >
    <div className={cn("-my-6 flex min-h-0 flex-col overflow-hidden", PREPACK_TRANSFER_KIOSK_HEIGHT_CLASS)}>
      <header className="flex shrink-0 items-center gap-2 border-b border-slate-200 bg-slate-50 px-1 py-2">
        <div className="min-w-0 flex-1">
          {trail.length > 0 ? (
            <HubBreadcrumbTitleRow
              className="min-w-0"
              title={<span className="truncate text-lg font-semibold text-slate-900">{orderLabel}</span>}
              helpGlyph={<HoverHintWithGlyph hoverHint={RECEIVE_PAGE_HOVER_HINT} />}
            />
          ) : (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <h1 className="truncate text-lg font-semibold text-slate-900">{pageTitle}</h1>
              <HoverHintWithGlyph hoverHint={RECEIVE_PAGE_HOVER_HINT} />
            </div>
          )}
          {arrivalContainerHeading != null || (receiptReadOnly && activeReceipt != null) ? (
            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm font-semibold text-slate-800">
              {receiptReadOnly && activeReceipt != null ? (
                <span>Receipt {activeReceipt.id}</span>
              ) : null}
              {arrivalContainerHeading != null ? <span>{arrivalContainerHeading}</span> : null}
              {labeledContainerCount > 0 ? (
                <Badge tone="neutral">
                  {labeledContainerCount} subcontainer{labeledContainerCount === 1 ? "" : "s"}
                </Badge>
              ) : null}
            </p>
          ) : labeledContainerCount > 0 ? (
            <p className="mt-0.5">
              <Badge tone="neutral">
                {labeledContainerCount} subcontainer{labeledContainerCount === 1 ? "" : "s"}
              </Badge>
            </p>
          ) : null}
          {(detail.linked_transfers ?? []).length > 0 ? (
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0 text-xs text-slate-600">
              {(detail.linked_transfers ?? []).map((transfer) => (
                <Link
                  key={transfer.id}
                  to={`/manufacturing/transfers?expand=${transfer.id}`}
                  title={`Transfer ${transfer.number} (${transfer.status}) is waiting on this PO's stock - open it in the transfers workbench`}
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-sky-50 px-2 py-0.5 font-semibold text-sky-800 ring-1 ring-sky-200 hover:bg-sky-100"
                >
                  Feeds {transfer.number}
                  <span className="font-normal text-sky-600">({transfer.status})</span>
                </Link>
              ))}
            </div>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-end gap-2">
          <LaborStationOperatorPresence stationPage="receiving" receivingFloor="receive" />
          <InboundZplPrinterHeaderButton
            station={station}
            onStationChange={setStation}
            unavailableMessage={unavailableMessage}
          
      documents={documents}
    />
          <span
            className={cn(
              "inline-flex touch-manipulation",
              finishArrivalBlockReason != null && "cursor-not-allowed",
            )}
            onClick={() => {
              if (finishArrivalBlockReason == null) return;
              showToast({ message: finishArrivalBlockReason, tone: "warning" });
            }}
          >
            <Button
              type="button"
              variant="primary"
              className={cn(
                "min-h-10 shrink-0 px-4 text-sm font-semibold",
                finishArrivalBlockReason != null && "pointer-events-none",
              )}
              disabled={finishArrivalBlockReason != null || finishArrivalMutation.isPending}
              aria-busy={finishArrivalMutation.isPending}
              onClick={() => finishArrivalMutation.mutate()}
            >
              <span className="relative inline-flex items-center justify-center">
                <span className={finishArrivalMutation.isPending ? "invisible" : undefined}>
                  Finish arrival container
                </span>
                {finishArrivalMutation.isPending ? (
                  <Loader2 className="absolute h-4 w-4 animate-spin" aria-hidden />
                ) : null}
              </span>
            </Button>
          </span>
          <TransferPackNumpadSideToggle side={numpadSide} onSideChange={setNumpadSide} />
        </div>
      </header>

      <section
        aria-label="Order lines"
        className="shrink-0 border-b border-slate-200 bg-white px-1 py-1.5"
      >
        <div className="mb-1.5 flex flex-wrap items-center gap-2 px-1">
          <label className="min-w-[14rem] max-w-full flex-1">
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 sm:h-5 sm:w-5"
                aria-hidden
              />
              <input
                ref={scanInputRef}
                type="text"
                value={lineQuery}
                aria-label={INBOUND_RECEIVE_LINE_SCAN_PROMPT.label}
                onChange={(event) => setLineQuery(event.target.value)}
                onKeyDown={(event) => {
                  const noted = appendAltNumpadDigit(altNumpadRef.current, event.code, event.altKey);
                  if (noted.consumed) {
                    altNumpadRef.current = noted.buffer;
                    event.preventDefault();
                    return;
                  }
                  if (event.key !== "Enter") return;
                  event.preventDefault();
                  const raw = event.currentTarget.value;
                  void (async () => {
                    if (await applyScannedQuantity(raw)) return;
                    await handleScan(raw);
                  })();
                }}
                onKeyUp={(event) => {
                  if (event.key !== "Alt") return;
                  const separator = characterForCompletedAltNumpad(altNumpadRef.current);
                  altNumpadRef.current = "";
                  if (separator == null) return;
                  setLineQuery((current) => `${current}${separator}`);
                }}
                placeholder={INBOUND_RECEIVE_LINE_SCAN_PROMPT.placeholder}
                autoComplete="off"
                disabled={receiptReadOnly}
                className={cn(
                  "min-h-11 w-full rounded-md border border-slate-300 py-2 pl-10 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-50 disabled:text-slate-500 sm:min-h-12 sm:pl-11 sm:text-base",
                  lineQuery.trim() !== "" ? "pr-10" : "pr-3",
                )}
                {...FLOOR_PAGE_SCAN_TARGET_PROPS}
                onFocus={() => {
                  if (receiptReadOnly) return;
                  setInputMode("scan");
                  setScanFeedback(null);
                }}
              />
              {lineQuery.trim() !== "" && !receiptReadOnly ? (
                <button
                  type="button"
                  className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800 touch-manipulation"
                  aria-label="Clear line search"
                  onClick={() => {
                    setLineQuery("");
                    setScanFeedback(null);
                    scanInputRef.current?.focus();
                  }}
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              ) : null}
            </div>
          </label>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <InboundReceiveDockActionsMenu
              disabled={receiptReadOnly || exceptionPending || detailQuery.isLoading}
              disabledReason={inboundReceiveMoreActionsTapReason({
                receiptReadOnly,
                busy: exceptionPending || detailQuery.isLoading,
              })}
              onAmlLookup={() => setAmlLookupModalOpen(true)}
              onUnlistedSku={() => setUnlistedModalOpen(true)}
              onNonSkuItem={() => setNonSkuModalOpen(true)}
            />
            {scanFeedback != null && unlistedSkuPrefill.trim() !== "" ? (
              <TapDisabledReason
                reason={inboundReceiveMoreActionsTapReason({
                  receiptReadOnly,
                  busy: exceptionPending || detailQuery.isLoading,
                })}
              >
                <Button
                  type="button"
                  variant="primary"
                  className="min-h-10 px-3 text-xs font-semibold"
                  disabled={receiptReadOnly || exceptionPending || detailQuery.isLoading}
                  onClick={() => setUnlistedModalOpen(true)}
                >
                  Add {unlistedSkuPrefill}
                </Button>
              </TapDisabledReason>
            ) : null}
          </div>
        </div>
        {scanFeedback != null ? (
          <p className="mb-1 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" role="alert">
            {scanFeedback}
          </p>
        ) : null}
        <div className="overflow-x-auto overflow-y-hidden pb-0.5">
          <div className="grid w-max auto-cols-[13.5rem] grid-flow-col gap-2 px-1">
            {filteredLines.length === 0 ? (
              <p className="px-2 py-2 text-xs text-slate-500">
                {lineQuery.trim() !== "" ? "No lines match this search." : "No lines on this order."}
              </p>
            ) : (
              filteredLines.map((line) => {
                const lineId = resolveInboundReceiveLineId(line, lines);
                const selected =
                  selectedLine != null && inboundLinesShareSku(line, selectedLine);
                return (
                  <InboundReceiveLineTile
                    key={lineId}
                    line={line}
                    selected={selected}
                    disabled={receiptReadOnly || closeSubcontainerMutation.isPending}
                    disabledReason={inboundReceiveLineTileTapReason({
                      receiptReadOnly,
                      closePending: closeSubcontainerMutation.isPending,
                    })}
                    openQuantity={openQuantityForDisplayLine(line)}
                    closedQuantity={closedQuantityForDisplayLine(line)}
                    onSelect={() => {
                      if (selected) {
                        setSelectedLineId(null);
                        setQuantityDraft("");
                        returnToPickMode();
                      } else {
                        selectLine(lineId);
                      }
                    }}
                  />
                );
              })
            )}
          </div>
        </div>
      </section>

      <div className={cn("min-h-0 flex-1 overflow-hidden pt-2", kioskNumpadSplitGridClass(numpadOnRight))}>
        <section
          aria-label="Labeled containers"
          className={cn(
            "flex min-h-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white p-2",
            kioskMainPaneOrderClass(numpadOnRight),
          )}
        >
          <div className="shrink-0 border-b border-slate-100 pb-1.5">
            <h2 className="text-sm font-semibold text-slate-900">Labeled containers</h2>
            {receiptReadOnly ? (
              <p className="mt-1 text-[11px] text-emerald-800" role="status">
                Finished crate — view only. Select a 4x2 printer above, then tap Reprint on a sealed container.
              </p>
            ) : null}
          </div>
          <div className="h-0 min-h-0 flex-1 overflow-hidden">
            <InboundReceiveSubcontainersPanel
              subcontainers={subcontainers}
              unitOfMeasureBySourceItemId={unitOfMeasureBySourceItemId}
              openLines={openContainerLines}
              nextContainerNo={subcontainers.length + 1}
              openUnitKind={openSubcontainerUnitKind}
              openUnitKindUserSet={openSubcontainerUnitKindUserSet}
              onOpenUnitKindChange={(kind) => {
                void handleOpenSubcontainerUnitKindChange(kind);
              }}
              onSealedUnitKindChange={(subcontainerId, kind) => {
                void handleSealedSubcontainerUnitKindChange(subcontainerId, kind);
              }}
              sealedUnitKindPendingId={sealedUnitKindPendingId}
              onUnseal={(subcontainerId) => {
                void handleUnsealSubcontainer(subcontainerId);
              }}
              unsealPendingId={unsealPendingId}
              closePending={closeSubcontainerMutation.isPending}
              printPendingNo={printingSubcontainerNo}
              onCloseAndPrint={handleCloseAndPrint}
              onReprint={(subcontainerNo, items) => void handleReprintSubcontainer(subcontainerNo, items)}
              onEditLine={startEditingCount}
              sealReady={sealReady}
              sealBlockReason={sealBlockReason}
              labelPrinterSelected={labelPrinterSelected}
              readOnly={receiptReadOnly}
            />
          </div>
          <div className="shrink-0 space-y-2 border-t border-slate-100 pt-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative shrink-0" ref={packingSlipMenuRef}>
                <Button
                  type="button"
                  variant="secondary"
                  className="h-11 gap-1.5 px-3 text-sm font-medium"
                  disabled={uploadMutation.isPending || packingSlipDeleteMutation.isPending}
                  aria-haspopup="menu"
                  aria-expanded={packingSlipMenuOpen}
                  onClick={() => setPackingSlipMenuOpen((open) => !open)}
                >
                  {uploadMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <Upload className="h-4 w-4" aria-hidden />
                  )}
                  Packing slip
                  {packingSlipCount > 0 ? (
                    <Badge tone="neutral" className="min-w-[1.25rem] justify-center px-1.5 py-0 text-[11px]">
                      {packingSlipCount}
                    </Badge>
                  ) : null}
                  <ChevronDown className="h-4 w-4 text-slate-500" aria-hidden />
                </Button>
                {packingSlipMenuOpen ? (
                  <div
                    role="menu"
                    className="absolute bottom-full left-0 z-20 mb-1 min-w-[16rem] overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
                  >
                    {!receiptReadOnly ? (
                      <>
                        <button
                          type="button"
                          role="menuitem"
                          className="flex min-h-12 w-full items-center gap-2 px-3 py-3 text-left text-base text-slate-800 hover:bg-slate-50"
                          onClick={() => {
                            setPackingSlipMenuOpen(false);
                            packingSlipUploadInputRef.current?.click();
                          }}
                        >
                          <Upload className="h-5 w-5 shrink-0 text-slate-500" aria-hidden />
                          Upload file(s)
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          className="flex min-h-12 w-full items-center gap-2 px-3 py-3 text-left text-base text-slate-800 hover:bg-slate-50"
                          onClick={() => {
                            setPackingSlipMenuOpen(false);
                            setPackingSlipCameraOpen(true);
                          }}
                        >
                          <Camera className="h-5 w-5 shrink-0 text-slate-500" aria-hidden />
                          Capture page with camera
                        </button>
                      </>
                    ) : null}
                    {packingSlips.length > 0 ? (
                      <div className="my-1 border-t border-slate-100" role="separator" />
                    ) : null}
                    {packingSlips.map((slip, index) => (
                      <div
                        key={slip.id}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-slate-800"
                        role="none"
                      >
                        <span className="min-w-0 flex-1 truncate text-base" title={slip.filename}>
                          Page {packingSlips.length - index}
                        </span>
                        <button
                          type="button"
                          className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50"
                          aria-label={`View packing slip ${String(packingSlips.length - index)}`}
                          title={`View packing slip ${String(packingSlips.length - index)}`}
                          onClick={() => handleViewPackingSlip(slip.id)}
                        >
                          <Eye className="h-6 w-6" aria-hidden />
                        </button>
                        {!receiptReadOnly ? (
                          <button
                            type="button"
                            className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-slate-200 text-rose-700 hover:bg-rose-50"
                            aria-label={`Delete packing slip ${String(packingSlips.length - index)}`}
                            title={`Delete packing slip ${String(packingSlips.length - index)}`}
                            disabled={packingSlipDeleteMutation.isPending}
                            onClick={() => handleDeletePackingSlip(slip.id)}
                          >
                            <Trash2 className="h-6 w-6" aria-hidden />
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}
                <input
                  ref={packingSlipUploadInputRef}
                  type="file"
                  accept="application/pdf,image/png,image/jpeg,image/webp"
                  multiple
                  className="sr-only"
                  onChange={handlePackingSlipFilePick}
                />
              </div>
              <div className="flex min-w-[12rem] flex-1 items-stretch gap-2">
                <input
                  value={notes}
                  onChange={(event) => {
                    if (receiptReadOnly) return;
                    setNotes(event.target.value);
                  }}
                  placeholder="Receipt notes (damage, missing cartons)..."
                  aria-label="Receipt notes"
                  disabled={receiptReadOnly}
                  readOnly={receiptReadOnly}
                  className="h-11 min-w-0 flex-1 rounded-md border border-slate-300 py-2 pl-3 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-50 disabled:text-slate-600"
                />
                {!receiptReadOnly ? (
                  <VoiceToTextMicButton
                    className="h-11 w-11"
                    value={notes}
                    ariaLabel="Dictate receipt notes"
                    onValueChange={(next) => {
                      setNotes(next);
                    }}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </section>

        <section
          aria-label="Count quantity"
          className={cn(
            "flex min-h-0 flex-col gap-2 overflow-hidden rounded-lg border border-slate-200 bg-white p-2",
            kioskNumpadPaneOrderClass(numpadOnRight),
          )}
        >
          <InboundQuantityReadout
            selectedLine={selectedLine}
            currentCount={selectedLine != null ? openQuantityForDisplayLine(selectedLine) : 0}
            quantityDraft={quantityDraft}
            replacing={editingReplace}
          />
          {selectedLine != null ? (
            <InboundReceiveMpnScan line={selectedLine} draft={selectedLineDraft} />
          ) : null}
          {selectedLine != null && isOrderLine(selectedLine) && editingReplace ? (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <TapDisabledReason
                reason={inboundReceiveDamagedTapReason({
                  receiptReadOnly,
                  hasScannedArrivalPackage,
                  exceptionPending,
                })}
              >
                <Button
                  type="button"
                  variant="secondary"
                  className="min-h-10 gap-1.5 px-3 text-sm font-semibold"
                  disabled={receiptReadOnly || !hasScannedArrivalPackage || exceptionPending}
                  onClick={() => setDamagedModalOpen(true)}
                >
                  Damaged
                </Button>
              </TapDisabledReason>
              <TapDisabledReason
                reason={inboundReceiveClearLineTapReason({
                  receiptReadOnly,
                  openQuantity: openQuantityForDisplayLine(selectedLine),
                })}
              >
                <Button
                  type="button"
                  variant="secondary"
                  className="min-h-10 gap-1.5 px-3 text-sm font-semibold"
                  disabled={
                    receiptReadOnly || openQuantityForDisplayLine(selectedLine) <= 0
                  }
                  onClick={() => void clearCount(selectedLine.source_item_id)}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                  Clear line
                </Button>
              </TapDisabledReason>
            </div>
          ) : null}
          <div className="min-h-0 flex-1">
            <TransferPackNumpad
              fillHeight
              fillHeightFrom="sm"
              disabled={
                receiptReadOnly
                || selectedLineId == null
                || !mpnEntryAllowsCounting(selectedLineMpnState)
                || closeSubcontainerMutation.isPending
              }
              disabledReason={inboundReceiveNumpadTapReason({
                receiptReadOnly,
                lineSelected: selectedLineId != null,
                mpnAllowsCounting: mpnEntryAllowsCounting(selectedLineMpnState),
                closePending: closeSubcontainerMutation.isPending,
              })}
              onDigit={appendDigit}
              onClear={() => {
                if (selectedLineId == null) return;
                setQuantityDraft("");
              }}
              onBackspace={() => {
                if (selectedLineId == null) return;
                setQuantityDraft((current) => current.slice(0, -1));
              }}
              onEnter={submitQuantity}
            />
          </div>
        </section>
      </div>
      <CameraCaptureModal
        open={packingSlipCameraOpen}
        title="Packing slip page"
        hint="Capture one page at a time. After you save, use Save and capture another for the next page."
        onClose={() => setPackingSlipCameraOpen(false)}
        onCapture={(file) => {
          setPackingSlipCameraOpen(false);
          queuePackingSlipForConfirm(file, `Page ${String(packingSlipCount + 1)}`);
        }}
      />
      <InboundPackingSlipConfirmModal
        open={packingSlipConfirmOpen}
        file={pendingPackingSlipFile}
        pageLabel={pendingPackingSlipPageLabel}
        confirming={uploadMutation.isPending}
        onConfirm={() => handleConfirmPackingSlip(false)}
        onConfirmAndAddAnother={
          receiptReadOnly ? undefined : () => handleConfirmPackingSlip(true)
        }
        onCancel={handleCancelPackingSlipConfirm}
        onTryAgain={receiptReadOnly ? undefined : handleTryAgainPackingSlipConfirm}
      />
      <InboundReceiveArrivalOrderPickModal
        open={arrivalOrderPick != null}
        packageNo={arrivalOrderPick?.packageNo ?? 0}
        vendorName={arrivalOrderPick?.vendorName}
        sources={arrivalOrderPick?.sources ?? []}
        onSelect={handleArrivalOrderPick}
        onCancel={() => setArrivalOrderPick(null)}
      />
      <InboundReceiveDamagedModal
        open={damagedModalOpen}
        line={selectedLine}
        currentGoodQuantity={
          selectedLine != null ? countedQuantity(drafts[selectedLine.source_item_id]) : 0
        }
        pending={exceptionPending}
        onClose={() => setDamagedModalOpen(false)}
        onSubmit={(input) => void handleDamagedSplit(input)}
      />
      <InboundReceiveUnlistedSkuModal
        open={unlistedModalOpen}
        initialSku={unlistedSkuPrefill}
        pending={exceptionPending}
        onClose={() => setUnlistedModalOpen(false)}
        onSubmit={(input) => void handleAddUnlistedSku(input)}
      />
      <InboundReceiveNonSkuModal
        open={nonSkuModalOpen}
        pending={exceptionPending}
        onClose={() => setNonSkuModalOpen(false)}
        onSubmit={(input) => void handleAddNonSkuItem(input)}
      />
      <InboundReceiveMpnEntryModal
        open={mpnEntryModalOpen}
        sku={selectedLine?.sku ?? ""}
        verifying={mpnVerifying}
        seedMpn={manufacturerSeed}
        onVerify={(mpn, manufacturerName, labelQuantity) => {
          const quantityToApply = labelQuantity ?? pendingSupplierLabelQuantityRef.current;
          pendingSupplierLabelQuantityRef.current = null;
          setManufacturerSeed(null);
          void applyScannedMpn(mpn, undefined, manufacturerName, quantityToApply);
        }}
        onClose={() => {
          pendingSupplierLabelQuantityRef.current = null;
          setManufacturerSeed(null);
          setSelectedLineId(null);
          setQuantityDraft("");
          returnToPickMode();
        }}
      />
      <InboundReceiveMpnMismatchModal
        open={selectedLine != null && selectedLineMpnState === "blocked" && !receiptReadOnly}
        sku={selectedLine?.sku ?? null}
        scannedMpn={selectedLineDraft.scanned_mpn}
        disabled={receiptReadOnly}
        onRetry={() => {
          if (selectedLine == null) return;
          setMpnVerifying(false);
          updateDraft(selectedLine.source_item_id, {
            ...emptyInboundLineMpnDraftFields(),
            received_quantity: "",
          });
        }}
      />
      <InboundReceiveAmlLookupModal
        open={amlLookupModalOpen}
        onClose={() => setAmlLookupModalOpen(false)}
        onSelectCpn={focusOrderLineFromCpn}
      />
    </div>
    </InboundContainerLaborProvider>
  );
}
